/** API di dominio (game, staff, plot, carte, …) — client core condiviso. */
import {
  API_BASE_URL,
  fetchAuthenticated,
  fetchPublic
} from './core';
import { normalizeScannedQrId } from '../utils/qrScan';

export const getBodySlots = () => {
  // Possiamo hardcodarlo nel frontend per semplicità o fare una chiamata
  return [
      { code: 'HD1', name: 'Testa 1 (Cervello/Occhi)' },
      { code: 'HD2', name: 'Testa 2 (Volto/Orecchie)' },
      { code: 'TR1', name: 'Tronco 1 (Cuore/Polmoni)' },
      { code: 'TR2', name: 'Tronco 2 (Spina Dorsale/Pelle)' },
      { code: 'RA', name: 'Braccio Destro' },
      { code: 'LA', name: 'Braccio Sinistro' },
      { code: 'RL', name: 'Gamba Destra' },
      { code: 'LL', name: 'Gamba Sinistra' },
  ];
};

/**
 * Recupera la lista dei personaggi associati all'utente.
 */
export const getPersonaggiList = (onLogout, viewAll = false) => {
  const queryParam = viewAll ? '?view_all=true' : '';
  return fetchAuthenticated(`/api/personaggi/api/personaggi/${queryParam}`, { method: 'GET' }, onLogout);
};

/**
 * Recupera i dettagli di un personaggio specifico.
 */
export const getPersonaggioDetail = (id, onLogout) => {
  const cleanId = String(id ?? '').trim();
  if (!cleanId) {
    return Promise.reject(new Error('ID personaggio mancante.'));
  }
  return fetchAuthenticated(`/api/personaggi/api/personaggi/${cleanId}/`, { method: 'GET' }, onLogout);
};

/**
 * Snapshot compatto per modalità offline (stesso permesso del dettaglio personaggio).
 */
export const getPersonaggioGameState = (id, onLogout) => {
  const cleanId = String(id ?? '').trim();
  if (!cleanId) {
    return Promise.reject(new Error('ID personaggio mancante.'));
  }
  return fetchAuthenticated(`/api/personaggi/api/personaggi/${cleanId}/game_state/`, { method: 'GET' }, onLogout);
};

export const getQrCodeData = (qrId, onLogout, personaggioId = null, minigiocoSessionId = null, opts = {}) => {
  const cleanId = normalizeScannedQrId(qrId);
  const params = new URLSearchParams();
  if (personaggioId != null && personaggioId !== '') {
    params.set('personaggio_id', String(personaggioId));
  }
  if (minigiocoSessionId) {
    params.set('minigioco_session_id', String(minigiocoSessionId));
  }
  if (opts.pilotRipara) {
    params.set('pilot_ripara', '1');
  }
  const q = params.toString() ? `?${params.toString()}` : '';
  return fetchAuthenticated(`/api/personaggi/api/qrcode/${cleanId}/${q}`, { method: 'GET' }, onLogout);
};

export const pilotSubsystemRepair = (
  qrId,
  personaggioId,
  onLogout,
  minigiocoSessionId = null,
  componentiScelti = null,
) =>
  fetchAuthenticated(
    '/api/pilot/subsystems/qr-repair/',
    {
      method: 'POST',
      body: JSON.stringify({
        qr_id: normalizeScannedQrId(qrId),
        personaggio_id: personaggioId,
        ...(minigiocoSessionId ? { minigioco_session_id: minigiocoSessionId } : {}),
        ...(Array.isArray(componentiScelti) && componentiScelti.length
          ? { componenti_scelti: componentiScelti }
          : {}),
      }),
    },
    onLogout,
  );

export const pilotSubsystemRecharge = (qrId, personaggioId, onLogout, componentiScelti = null) =>
  fetchAuthenticated(
    '/api/pilot/subsystems/qr-recharge/',
    {
      method: 'POST',
      body: JSON.stringify({
        qr_id: normalizeScannedQrId(qrId),
        personaggio_id: personaggioId,
        ...(Array.isArray(componentiScelti) && componentiScelti.length
          ? { componenti_scelti: componentiScelti }
          : {}),
      }),
    },
    onLogout,
  );

export const pilotSubsystemSabota = (qrId, personaggioId, onLogout) =>
  fetchAuthenticated(
    '/api/pilot/subsystems/qr-action/',
    {
      method: 'POST',
      body: JSON.stringify({
        qr_id: normalizeScannedQrId(qrId),
        personaggio_id: personaggioId,
        azione: 'sabota',
      }),
    },
    onLogout,
  );

export const minigiocoComplete = (sessionId, personaggioId, stato, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/minigioco/${sessionId}/complete/`,
    {
      method: 'POST',
      body: JSON.stringify({ personaggio_id: personaggioId, stato }),
    },
    onLogout
  );

export const minigiocoExpire = (sessionId, personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/minigioco/${sessionId}/expire/`,
    {
      method: 'POST',
      body: JSON.stringify({ personaggio_id: personaggioId }),
    },
    onLogout
  );

export const staffGetMinigiocoQrConfig = (qrId, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/minigioco-qr/${qrId}/`, { method: 'GET' }, onLogout);

export const staffSaveMinigiocoQrConfig = (qrId, formData, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/minigioco-qr/${qrId}/`,
    { method: 'PUT', body: formData },
    onLogout
  );

export const staffGetMinigiocoBiblioteca = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/minigioco-biblioteca/', { method: 'GET' }, onLogout);

export const staffAggiornaMinigiocoBiblioteca = (target = 100, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/minigioco-biblioteca/aggiorna/',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target }),
    },
    onLogout
  );

export const OPENVERSE_REGISTER_URL = 'https://api.openverse.org/v1/auth_tokens/register/';

/** Registrazione Openverse dal browser (bypass Cloudflare VPS). */
export async function openverseRegisterFromBrowser({ name, description, email }) {
  const resp = await fetch(OPENVERSE_REGISTER_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      name: (name || '').trim(),
      description: (description || '').trim(),
      email: (email || '').trim(),
    }),
  });
  const text = await resp.text();
  let data = null;
  try {
    data = JSON.parse(text);
  } catch {
    data = null;
  }
  if (!resp.ok) {
    const cloudflare = /just a moment/i.test(text) || (/cloudflare/i.test(text) && /<!doctype html/i.test(text));
    const err = new Error(
      cloudflare
        ? 'Openverse bloccato da Cloudflare anche dal browser. Incolla le credenziali manualmente o riprova da un\'altra rete.'
        : `Openverse HTTP ${resp.status}: ${text.slice(0, 200)}`
    );
    err.cloudflare = cloudflare;
    throw err;
  }
  if (!data?.client_id || !data?.client_secret) {
    throw new Error('Risposta Openverse incompleta (mancano client_id o client_secret).');
  }
  return data;
}

export const staffSalvaOpenverseMinigioco = (payload, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/minigioco-biblioteca/openverse/salva/',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    onLogout
  );

export const staffRegistraOpenverseMinigioco = (payload, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/minigioco-biblioteca/openverse/registra/',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    onLogout
  );

export const staffVerificaOpenverseMinigioco = (onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/minigioco-biblioteca/openverse/verifica/',
    { method: 'POST' },
    onLogout
  );

export const staffInspectQrCode = (qrId, onLogout) => {
  return fetchAuthenticated(`/api/personaggi/api/staff/qr-inspect/${qrId}/`, { method: 'GET' }, onLogout);
};

export const staffQrInventarioScan = (payload, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/staff/qr-inventario-scan/',
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout
  );
};

/**
 * Richiede un oggetto da un inventario (azione "Prendi") - Sistema legacy
 */
export const richiediTransazione = (oggettoId, mittenteInventarioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/transazioni/richiedi/', 
    {
      method: 'POST',
      body: JSON.stringify({
        oggetto_id: oggettoId,
        mittente_id: mittenteInventarioId,
      })
    },
    onLogout
  );
};

/**
 * Crea una nuova transazione avanzata con proposta iniziale
 */
export const createTransazioneAvanzata = (destinatarioId, proposta, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/transazioni/avanzata/',
    {
      method: 'POST',
      body: JSON.stringify({
        destinatario_id: destinatarioId,
        proposta: proposta
      })
    },
    onLogout
  );
};

/**
 * Recupera il dettaglio di una transazione con tutte le proposte
 */
export const getTransazioneDetail = (transazioneId, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/transazioni/${transazioneId}/`,
    { method: 'GET' },
    onLogout
  );
};

/**
 * Aggiunge una proposta (controproposta o rilancio) a una transazione
 */
export const addPropostaTransazione = (transazioneId, proposta, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/transazioni/${transazioneId}/proposta/`,
    {
      method: 'POST',
      body: JSON.stringify(proposta)
    },
    onLogout
  );
};

/**
 * Accetta o rifiuta una transazione
 */
export const confermaTransazione = (transazioneId, azione, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/transazioni/${transazioneId}/conferma/`,
    {
      method: 'POST',
      body: JSON.stringify({ azione: azione }) // 'accetta' o 'rifiuta'
    },
    onLogout
  );
};

/**
 * Tenta di rubare un oggetto da un personaggio (azione "Ruba").
 */
export const rubaOggetto = (oggettoId, targetPersonaggioId, onLogout, personaggioId = null) => {
  const body = {
    oggetto_id: oggettoId,
    target_personaggio_id: targetPersonaggioId,
  };
  if (personaggioId != null && personaggioId !== '') body.personaggio_id = personaggioId;
  return fetchAuthenticated(
    '/api/personaggi/api/transazioni/ruba/', 
    {
      method: 'POST',
      body: JSON.stringify(body)
    },
    onLogout
  );
};

/**
 * Acquisisce un oggetto/attivata da un QR code.
 */
export const acquisisciItem = (qrCodeId, onLogout, personaggioId = null, minigiocoSessionId = null) => {
  const body = { qrcode_id: qrCodeId };
  if (personaggioId != null && personaggioId !== '') body.personaggio_id = personaggioId;
  if (minigiocoSessionId) body.minigioco_session_id = String(minigiocoSessionId);
  return fetchAuthenticated(
    '/api/personaggi/api/transazioni/acquisisci/', 
    {
      method: 'POST',
      body: JSON.stringify(body)
    },
    onLogout
  );
};

/**
 * Recupera la lista master di tutte le abilità.
 * @deprecated Non più usata, sostituita da getAcquirableSkills e dati da getPersonaggioDetail
 */
export const getAbilitaMasterList = (onLogout) => {
  return fetchAuthenticated('/api/personaggi/api/abilita/master_list/', { method: 'GET' }, onLogout);
};

/**
 * Tenta di acquisire un'abilità per il personaggio loggato.
 */
export const acquireAbilita = (abilitaId, characterId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/acquisisci_abilita/', 
    {
      method: 'POST',
      body: JSON.stringify({
        abilita_id: abilitaId,
        personaggio_id: characterId, // <-- NUOVO: Passa l'ID al backend
      })
    },
    onLogout
  );
};

/**
 * Revoca l'acquisto di un'abilità.
 */
export const revokeAbilita = (abilitaId, characterId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/revoca_abilita/',
    {
      method: 'POST',
      body: JSON.stringify({
        abilita_id: abilitaId,
        personaggio_id: characterId,
      }),
    },
    onLogout
  );
};

/**
 * Recupera la lista di tutti i punteggi (Caratteristiche, Statistiche, ecc).
 */
export const getPunteggiList = (onLogout) => {
  return fetchAuthenticated('/api/personaggi/api/punteggi/all/', { method: 'GET' }, onLogout);
};

/**
 * Recupera la lista dei contenitori statistiche (configurazione scheda).
 * GET /api/personaggi/api/statistiche/containers/
 */
export const getStatisticaContainers = (onLogout) => {
  return fetchAuthenticated('/api/personaggi/api/statistiche/containers/', { method: 'GET' }, onLogout);
};


// --- MODIFICA CHIAVE ---
// Questa è la versione corretta della funzione che usa il tuo
// helper 'fetchAuthenticated' e l'URL corretto.
// Sostituisce la versione errata che ti avevo dato.

/**
 * Recupera la lista *filtrata* di abilità acquistabili per il personaggio.
 * GET /api/personaggi/api/personaggio/me/abilita_acquistabili/
 */
export const getAcquirableSkills = (onLogout, selectedCharacterId) => {
  // Aggiunge l'ID del personaggio come query parameter. Il backend dovrà leggere 'char_id'.
  const queryParam = selectedCharacterId ? `?char_id=${selectedCharacterId}` : '';
  
  return fetchAuthenticated(
    `/api/personaggi/api/personaggio/me/abilita_acquistabili/${queryParam}`, // <--- AGGIUNTO queryParam
    { method: 'GET' }, 
    onLogout
  );
};

/**
 * GET /api/personaggi/api/messaggi/ - Ottiene la lista dei messaggi per il PG loggato.
 */
export const getMessages = (personaggioId, onLogout) => {
  if (!personaggioId) {
    return Promise.resolve([]); // Ritorna un array vuoto se l'ID non è fornito
  }
  const url = `/api/personaggi/api/messaggi/?personaggio_id=${personaggioId}`;

  return fetchAuthenticated(url, { method: 'GET' }, onLogout);
};

export const getMessageUnreadCounts = (onLogout) => {
  return fetchAuthenticated('/api/personaggi/api/messaggi/unread_counts/', { method: 'GET' }, onLogout);
};

/**
 * POST /api/personaggi/api/messaggi/broadcast/send/ - Invia un messaggio Broadcast.
 */
export const postBroadcastMessage = (messageData, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/messaggi/broadcast/send/',
    {
      method: 'POST',
      body: JSON.stringify(messageData)
    },
    onLogout
  );
};

/**
 * GET /api/personaggi/api/messaggi/admin/sent/ - Ottiene i messaggi inviati dall'admin.
 */
export const getAdminSentMessages = (onLogout) => {
  return fetchAuthenticated('/api/personaggi/api/messaggi/admin/sent/', { method: 'GET' }, onLogout);
};

export const getStaffMessaggiModelli = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/messaggi-modelli/', { method: 'GET' }, onLogout);

export const createStaffMessaggioModello = (payload, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/messaggi-modelli/',
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout,
  );

export const updateStaffMessaggioModello = (id, payload, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/messaggi-modelli/${id}/`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    onLogout,
  );

export const deleteStaffMessaggioModello = (id, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/messaggi-modelli/${id}/`,
    { method: 'DELETE' },
    onLogout,
  );

export const postMessaggioEventoInvio = (payload, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/messaggi/staff/evento-invio/',
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout,
  );

export const getStaffPoolPgMeta = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/pool-pg/meta/', { method: 'GET' }, onLogout);

export const getStaffPoolPgList = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/pool-pg/', { method: 'GET' }, onLogout);

export const createStaffPoolPg = (payload, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/pool-pg/',
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout,
  );

export const updateStaffPoolPg = (id, payload, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/pool-pg/${id}/`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    onLogout,
  );

export const deleteStaffPoolPg = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/pool-pg/${id}/`, { method: 'DELETE' }, onLogout);

export const getStaffPoolPgPersonaggi = (id, params, onLogout) => {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.evento_id) qs.set('evento_id', params.evento_id);
  if (params?.escludi_png != null) qs.set('escludi_png', params.escludi_png ? '1' : '0');
  const suffix = qs.toString() ? `?${qs}` : '';
  return fetchAuthenticated(
    `/api/personaggi/api/staff/pool-pg/${id}/personaggi/${suffix}`,
    { method: 'GET' },
    onLogout,
  );
};

export const setStaffPoolPgMembro = (id, payload, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/pool-pg/${id}/membri/`,
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout,
  );

export const setStaffPoolPgMembriBulk = (id, payload, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/pool-pg/${id}/membri-bulk/`,
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout,
  );

export const getStaffPoolPgConteggi = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/pool-pg/${id}/conteggi/`, { method: 'GET' }, onLogout);

export const getStaffPoolPgLog = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/pool-pg/${id}/log/`, { method: 'GET' }, onLogout);

export const postStaffPoolPgSorteggia = (id, payload, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/pool-pg/${id}/sorteggia/`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
    onLogout,
  );

export const getPoolPgGiocatoreVisibili = (personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/pool-pg/visibili/?personaggio_id=${personaggioId}`,
    { method: 'GET' },
    onLogout,
  );

export const getPoolPgGiocatoreDettaglio = (poolId, personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/pool-pg/${poolId}/?personaggio_id=${personaggioId}`,
    { method: 'GET' },
    onLogout,
  );

export const postPoolPgGiocatoreSorteggia = (poolId, personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/pool-pg/${poolId}/sorteggia/`,
    { method: 'POST', body: JSON.stringify({ personaggio_id: personaggioId }) },
    onLogout,
  );

export const getSorteggioAckPending = (personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/sorteggio-ack/pending/?personaggio_id=${personaggioId}`,
    { method: 'GET' },
    onLogout,
  );

export const postSorteggioAckConferma = (esitoId, payload, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/sorteggio-ack/${esitoId}/conferma/`,
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout,
  );

export const saveWebPushSubscription = async (subscription, onLogout) => {
  if (!subscription) {
    throw new Error('Sottoscrizione push mancante.');
  }
  const payload =
    typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription;
  return fetchAuthenticated(
    '/api/personaggi/api/webpush/subscribe/',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    onLogout
  );
};

/** Registra un device token FCM della shell Android Capacitor. */
export const saveFcmDeviceToken = async ({ token, platform = 'android', appId = '' }, onLogout) => {
  if (!token) {
    throw new Error('Token FCM mancante.');
  }
  return fetchAuthenticated(
    '/api/personaggi/api/fcm/register/',
    {
      method: 'POST',
      body: JSON.stringify({
        token,
        platform,
        app_id: appId || 'nativeapp.kor35.it',
      }),
    },
    onLogout
  );
};

// --- NUOVE FUNZIONI PER INFUSIONI E TESSITURE ---

/**
 * Recupera la lista delle infusioni acquistabili.
 */
export const getAcquirableInfusioni = (characterId) => {
  return fetchAuthenticated(
    `/api/personaggi/api/personaggio/me/infusioni_acquistabili/?char_id=${characterId}`, 
    { method: 'GET' }
  );
};

/**
 * Acquisisce un'infusione.
 */
export const acquireInfusione = (infusioneId, personaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/acquisisci_infusione/', 
    {
      method: 'POST',
      body: JSON.stringify({ 
        infusione_id: infusioneId, 
        personaggio_id: personaggioId 
      }),
    }, 
    onLogout
  );
};

/**
 * Revoca l'acquisto di un'infusione.
 */
export const revokeInfusione = (infusioneId, personaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/revoca_infusione/',
    {
      method: 'POST',
      body: JSON.stringify({
        infusione_id: infusioneId,
        personaggio_id: personaggioId,
      }),
    },
    onLogout
  );
};

/**
 * Recupera la lista delle tessiture acquistabili.
 */
export const getAcquirableTessiture = (characterId) => {
  return fetchAuthenticated(
    `/api/personaggi/api/personaggio/me/tessiture_acquistabili/?char_id=${characterId}`, 
    { method: 'GET' }
  );
};

/**
 * Acquisisce una tessitura.
 */
export const acquireTessitura = (tessituraId, personaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/acquisisci_tessitura/', 
    {
      method: 'POST',
      body: JSON.stringify({ 
        tessitura_id: tessituraId, 
        personaggio_id: personaggioId 
      }),
    }, 
    onLogout
  );
};

/**
 * Revoca l'acquisto di una tessitura.
 */
export const revokeTessitura = (tessituraId, personaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/revoca_tessitura/',
    {
      method: 'POST',
      body: JSON.stringify({
        tessitura_id: tessituraId,
        personaggio_id: personaggioId,
      }),
    },
    onLogout
  );
};

/**
 * Revoca l'acquisto di un cerimoniale.
 */
export const revokeCerimoniale = (cerimonialeId, personaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/revoca_cerimoniale/',
    {
      method: 'POST',
      body: JSON.stringify({
        cerimoniale_id: cerimonialeId,
        personaggio_id: personaggioId,
      }),
    },
    onLogout
  );
};

/**
 * Toggle favorite status di una tessitura.
 */
export const consumaConsumabile = (consumabileId, personaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/consuma_consumabile/',
    {
      method: 'POST',
      body: JSON.stringify({
        consumabile_id: consumabileId,
        personaggio_id: personaggioId,
      }),
    },
    onLogout
  );
};

export const toggleTessituraFavorite = (tessituraId, personaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/toggle_tessitura_favorite/', 
    {
      method: 'POST',
      body: JSON.stringify({ 
        tessitura_id: tessituraId, 
        personaggio_id: personaggioId 
      }),
    }, 
    onLogout
  );
};

/**
 * Avvia la creazione di un consumabile da tessitura (Alchimia). Costo e tempo dipendono dall'aura della tessitura.
 */
export const avviaCreazioneConsumabile = (tessituraId, personaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/avvia_creazione_consumabile/',
    {
      method: 'POST',
      body: JSON.stringify({ tessitura_id: tessituraId, personaggio_id: personaggioId }),
    },
    onLogout
  );
};

/**
 * Completa una creazione consumabile scaduta e aggiunge il consumabile all'inventario.
 * fetchAuthenticated restituisce già il body parsato, non la Response.
 */
export const completaCreazioneConsumabile = (personaggioId, creazioneId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/completa_creazione_consumabile/',
    {
      method: 'POST',
      body: JSON.stringify({ personaggio_id: personaggioId, creazione_id: creazioneId ?? null }),
    },
    onLogout
  );
};

export const getModelliAura = (auraId) => {
  return fetchAuthenticated(`/api/personaggi/api/punteggio/${auraId}/modelli/`, { method: 'GET' });
};

export const selezionaModelloAura = (personaggioId, modelloId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggio/me/seleziona_modello_aura/', 
    {
      method: 'POST',
      body: JSON.stringify({ personaggio_id: personaggioId, modello_id: modelloId })
    }, 
    onLogout
  );
};

export const getProposte = async (charId) => {
    // fetchAuthenticated restituisce già i dati, non la response raw.
    // L'errore viene gestito internamente a fetchAuthenticated.
    return await fetchAuthenticated(`/api/personaggi/api/proposte/?char_id=${charId}`, {
        method: 'GET'
    });
};

export const createProposta = async (data) => {
    return await fetchAuthenticated(`/api/personaggi/api/proposte/`, {
        method: 'POST',
        body: JSON.stringify(data)
    });
};

export const updateProposta = async (id, data) => {
    return await fetchAuthenticated(`/api/personaggi/api/proposte/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data)
    });
};

export const deleteProposta = async (id) => {
    // Qui fetchAuthenticated potrebbe tornare null (204 No Content), va bene così.
    await fetchAuthenticated(`/api/personaggi/api/proposte/${id}/`, {
        method: 'DELETE'
    });
    return true;
};

export const sendProposta = async (id) => {
    return await fetchAuthenticated(`/api/personaggi/api/proposte/${id}/invia_proposta/`, {
        method: 'POST'
    });
};

// --- UTILITIES PUNTEGGI ---

export const getAllPunteggi = async () => {
    // Assicurati che l'URL finisca con /all/ come definito in urls.py
    return await fetchAuthenticated(`/api/personaggi/api/punteggi/all/`, {
        method: 'GET'
    });
};

/**
 * Mattoni per aura; con personaggioId filtra i proibiti in base al modello di aura scelto dal PG.
 * Risposta normalizzata: { mattoni, obbligatorie_caratteristica_ids }.
 */
export const getMattoniAura = async (auraId, personaggioId = null) => {
    const q = personaggioId ? `?personaggio_id=${encodeURIComponent(personaggioId)}` : '';
    const raw = await fetchAuthenticated(`/api/personaggi/api/punteggio/${auraId}/mattoni/${q}`, {
        method: 'GET'
    });
    if (Array.isArray(raw)) {
        return { mattoni: raw, obbligatorie_caratteristica_ids: [] };
    }
    return {
        mattoni: raw?.mattoni ?? [],
        obbligatorie_caratteristica_ids: raw?.obbligatorie_caratteristica_ids ?? [],
    };
};

export const getAdminPendingProposalsCount = (onLogout) => {
  return fetchAuthenticated('/api/personaggi/api/admin/pending_proposals_count/', { method: 'GET' }, onLogout);
};

export const markMessageAsRead = (messageId, characterId, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/messaggi/${messageId}/leggi/`,
    {
      method: 'POST',
      body: JSON.stringify({ personaggio_id: characterId })
    },
    onLogout
  );
};

/**
 * Cancella un messaggio (soft delete per l'utente).
 * POST /api/personaggi/api/messaggi/<id>/cancella/
 */
export const deleteMessage = (messageId, characterId, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/messaggi/${messageId}/cancella/`,
    {
      method: 'POST',
      body: JSON.stringify({ personaggio_id: characterId })
    },
    onLogout
  );
};

/**
 * Recupera i log paginati.
 * @param {number} page - Numero di pagina (default 1)
 */
export const getPersonaggioLogs = (page = 1) => {
  return fetchAuthenticated(`/api/personaggi/api/personaggio/me/logs/?page=${page}`, { method: 'GET' });
};

/**
 * Recupera le transazioni paginate.
 * @param {number} page - Numero di pagina
 * @param {string} tipo - 'entrata' o 'uscita'
 * @param {string} charId - ID del personaggio (opzionale)
 */
export const getPersonaggioTransazioni = (page = 1, tipo = 'entrata', charId = null) => {
  let url = `/api/personaggi/api/personaggio/me/transazioni/?page=${page}&tipo=${tipo}`;
  if (charId) {
    url += `&char_id=${charId}`;
  }
  return fetchAuthenticated(url, { method: 'GET' });
}
/**
 * Cerca personaggi per nome (Autocomplete)
 * Opzionalmente filtra per compatibilità con un'infusione (Innesto).
 */
export const searchPersonaggi = (query, currentCharacterId, infusioneId = null, uso = null) => {
  let url = `/api/personaggi/api/personaggi/search/?q=${encodeURIComponent(query)}&current_char_id=${currentCharacterId}`;
  if (infusioneId) {
      url += `&infusione_id=${infusioneId}`;
  }
  if (uso) {
      url += `&uso=${encodeURIComponent(uso)}`;
  }
  return fetchAuthenticated(url, { method: 'GET' });
};

export const getPreferredPersonaggio = (onLogout) => {
  return fetchAuthenticated('/api/personaggi/api/personaggi/preferred/', { method: 'GET' }, onLogout);
};

export const setPreferredPersonaggio = (preferredPersonaggioId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/personaggi/preferred/',
    {
      method: 'POST',
      body: JSON.stringify({ preferred_personaggio_id: preferredPersonaggioId || null }),
    },
    onLogout
  );
};

/**
 * Invia un messaggio privato
 */
export const sendPrivateMessage = (messageData, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/messaggi/send/',
    {
      method: 'POST',
      body: JSON.stringify(messageData)
    },
    onLogout
  );
};

export const getChiamataIceServers = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/chiamate/ice-servers/', { method: 'GET' }, onLogout);

export const getChiamataVocaleAttiva = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/chiamate/', { method: 'GET' }, onLogout);

export const avviaChiamataVocale = (payload, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/chiamate/',
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout
  );

export const accettaChiamataVocale = (callId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/chiamate/${callId}/accetta/`,
    { method: 'POST', body: JSON.stringify({}) },
    onLogout
  );

export const rifiutaChiamataVocale = (callId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/chiamate/${callId}/rifiuta/`,
    { method: 'POST', body: JSON.stringify({}) },
    onLogout
  );

export const chiudiChiamataVocale = (callId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/chiamate/${callId}/chiudi/`,
    { method: 'POST', body: JSON.stringify({}) },
    onLogout
  );

export const getCodaChiamateStaff = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/chiamate/coda/', { method: 'GET' }, onLogout);

export const getStoricoChiamateVocali = (personaggioId, onLogout, { limit = 50 } = {}) =>
  fetchAuthenticated(
    `/api/personaggi/api/chiamate/storico/?personaggio_id=${encodeURIComponent(personaggioId)}&limit=${limit}`,
    { method: 'GET' },
    onLogout
  );

// Crea un oggetto fisico a partire da un'infusione
export const craftOggetto = (infusioneId) => {
  return fetchAuthenticated('/api/personaggi/api/oggetti/craft/', {
    method: 'POST',
    body: JSON.stringify({ infusione_id: infusioneId })
  });
};

// // Monta un potenziamento (Mod/Materia) su un oggetto ospite
// export const montaPotenziamento = (ospiteId, potenziamentoId) => {
//   return fetchAuthenticated(`/api/personaggi/api/oggetti/${ospiteId}/monta/`, {
//     method: 'POST',
//     body: JSON.stringify({ potenziamento_id: potenziamentoId })
//   });
// };

// Smonta un potenziamento
export const smontaPotenziamento = (ospiteId, potenziamentoId) => {
  return fetchAuthenticated(`/api/personaggi/api/oggetti/${ospiteId}/smonta/`, {
    method: 'POST',
    body: JSON.stringify({ potenziamento_id: potenziamentoId })
  });
};

// Usa una carica (Mod, Innesti, Oggetti)
export const usaCarica = (oggettoId) => {
  return fetchAuthenticated(`/api/personaggi/api/oggetti/${oggettoId}/usa_carica/`, {
    method: 'POST'
  });
};

// Ricarica oggetto (pagando crediti)
export const ricaricaOggetto = (oggettoId) => {
  return fetchAuthenticated(`/api/personaggi/api/oggetti/${oggettoId}/ricarica/`, {
    method: 'POST'
  });
};

// --- API INVENTARIO & OGGETTI ---

/**
 * Recupera i dettagli di un oggetto tramite ID
 * GET /api/personaggi/api/oggetti/<id>/
 */
export const getOggettoDetail = (oggettoId, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/oggetti/${oggettoId}/`,
    { method: 'GET' },
    onLogout
  );
};

/**
 * Equipaggia o disequipaggia un oggetto fisico.
 * POST /api/personaggi/api/oggetti/equipaggia/
 */
export const equipaggiaOggetto = (itemId, characterId, slotKey = null, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/oggetti/equipaggia/', 
    {
      method: 'POST',
      // Invia anche char_id nel JSON
      body: JSON.stringify({ 
          item_id: itemId,
          char_id: characterId,
          slot_key: slotKey,
      })
    },
    onLogout
  );
};

export const danneggiaOggetto = (itemId, characterId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/oggetti/danneggia/',
    {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        char_id: characterId,
      }),
    },
    onLogout
  );
};

export const riparaOggetto = (itemId, characterId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/oggetti/ripara/',
    {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        char_id: characterId,
      }),
    },
    onLogout
  );
};

export const scartaOggetto = (itemId, characterId, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/oggetti/scarta/',
    {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        char_id: characterId,
      }),
    },
    onLogout
  );
};

export const assemblaOggetto = (hostId, modId, characterId, useAcademy = false, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/oggetti/assembla/', 
    {
      method: 'POST',
      body: JSON.stringify({ 
        host_id: hostId, 
        mod_id: modId,
        char_id: characterId,
        use_academy: useAcademy // Parametro nuovo
      })
    },
    onLogout
  );
};

export const smontaOggetto = (hostId, modId, characterId, useAcademy = false, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/oggetti/smonta/', 
    {
      method: 'POST',
      body: JSON.stringify({ 
        host_id: hostId, 
        mod_id: modId,
        char_id: characterId,
        use_academy: useAcademy
      })
    },
    onLogout
  );
};

// Se hai bisogno del crafting nel frontend in futuro:
export const craftOggettoFromInfusione = (infusioneId, targetId, qrCode, onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/oggetti/craft/',
    {
      method: 'POST',
      body: JSON.stringify({
        infusione_id: infusioneId,
        target_id: targetId,
        qr_code: qrCode
      })
    },
    onLogout
  );
};

export const startForging = (infusioneId, charId, slotTarget = null) => {
  return fetchAuthenticated(
    '/api/personaggi/api/crafting/avvia_forgiatura/',
    {
      method: 'POST',
      body: JSON.stringify({ 
        infusione_id: infusioneId, 
        char_id: charId,
        slot_target: slotTarget
      })
    }
  );
};

export const completeForging = (forgiaturaId, charId) => {
  return fetchAuthenticated(
    '/api/personaggi/api/crafting/completa_forgiatura/',
    {
      method: 'POST',
      body: JSON.stringify({ 
        forgiatura_id: forgiaturaId, 
        char_id: charId 
      })
    }
  );
};

export const getForgingQueue = (charId) => {
  return fetchAuthenticated(
    `/api/personaggi/api/crafting/coda_forgiatura/?char_id=${charId}`,
    { method: 'GET' }
  );
};

// --- API NEGOZIO (SHOP) ---

export const getShopItems = (charId = null) => {
  const qs = charId ? `?char_id=${encodeURIComponent(charId)}` : '';
  return fetchAuthenticated(
    `/api/personaggi/api/negozio/listino/${qs}`,
    { method: 'GET' }
  );
};

export const buyShopItem = (oggettoId, charId) => {
  return fetchAuthenticated(
    '/api/personaggi/api/negozio/acquista/',
    {
      method: 'POST',
      body: JSON.stringify({ 
        oggetto_id: oggettoId, 
        char_id: charId 
      })
    }
  );
};

/**
 * Valida la compatibilità e le competenze per un assemblaggio.
 */
export const validateAssembly = (charId, hostId, modId) => {
  return fetchAuthenticated(
    '/api/personaggi/api/assembly/validate/', 
    {
      method: 'POST',
      body: JSON.stringify({
        char_id: charId,
        host_id: hostId,
        mod_id: modId
      })
    }
  );
};

/**
 * Crea una richiesta di lavoro (Installazione o Rimozione).
 * @param {string} operationType - 'INST' (Default) o 'RIMO'
 */
export const createAssemblyRequest = (charId, hostId, modId, artisanName, offer, operationType = 'INST') => {
  return fetchAuthenticated(
    '/api/personaggi/api/richieste-assemblaggio/crea/', 
    {
      method: 'POST',
      body: JSON.stringify({
        committente_id: charId,
        host_id: hostId,
        comp_id: modId,
        artigiano_nome: artisanName,
        offerta: offer,
        tipo_operazione: operationType // <--- Passa il tipo al backend
      })
    }
  );
};

/**
 * Accetta una richiesta di assemblaggio (Lato Artigiano).
 */
export const acceptAssemblyRequest = (requestId) => {
  return fetchAuthenticated(
    `/api/personaggi/api/richieste-assemblaggio/${requestId}/accetta/`, 
    { method: 'POST' }
  );
};

/**
 * Rifiuta una richiesta di assemblaggio (Lato Artigiano).
 */
export const rejectAssemblyRequest = (requestId) => {
  return fetchAuthenticated(
    `/api/personaggi/api/richieste-assemblaggio/${requestId}/rifiuta/`, 
    { method: 'POST' }
  );
};

/**
 * Recupera le richieste di assemblaggio (inviate o ricevute).
 */
export const getAssemblyRequests = () => {
  return fetchAuthenticated(
    '/api/personaggi/api/richieste-assemblaggio/', 
    { method: 'GET' }
  );
};

// Aggiungi questa funzione per recuperare gli artigiani capaci
// Questa va bene per entrambi (Assemblaggio e Forgiatura)
export const getCapableArtisans = (charId, hostId, modId, infusioneId) => {
  return fetchAuthenticated('/api/personaggi/api/assembly/artisans/', {
      method: 'POST',
      body: JSON.stringify({ 
        char_id: charId, 
        host_id: hostId, 
        mod_id: modId, 
        infusione_id: infusioneId 
      })
  });
};

export const forgiaOggetto = (infusioneId, charId, useAcademy = false) => {
  return fetchAuthenticated('/api/personaggi/api/oggetti/forgia/', {
      method: 'POST',
      body: JSON.stringify({ 
        infusione_id: infusioneId, 
        char_id: charId, 
        use_academy: useAcademy 
      })
  });
};

export const createForgingRequest = (charId, infusioneId, artisanName, offer) => {
  return fetchAuthenticated('/api/personaggi/api/richieste-assemblaggio/crea/', {
      method: 'POST',
      body: JSON.stringify({
        committente_id: charId,
        infusione_id: infusioneId,
        artigiano_nome: artisanName,
        offerta: offer,
        tipo_operazione: 'FORG'
      })
  });
};

export const validateForging = (charId, infusioneId) => {
  return fetchAuthenticated('/api/personaggi/api/forging/validate/', {
      method: 'POST',
      body: JSON.stringify({ 
        char_id: charId, 
        infusione_id: infusioneId 
      })
  });
};

export const getClassiOggetto = () => {
  return fetchAuthenticated('/api/personaggi/api/classi_oggetto/', { method: 'GET' });
};

/**
 * Esegue l'installazione di un innesto da una forgiatura completata.
 * (Operazione "Fai da te" o "Accettazione diretta")
 */
export const installaInnesto = (forgiaturaId, slot, characterId, targetId = null) => {
  return fetchAuthenticated(
    '/api/personaggi/api/crafting/completa_forgiatura/', 
    {
      method: 'POST',
      body: JSON.stringify({ 
        forgiatura_id: forgiaturaId, 
        char_id: characterId, // Chi esegue l'azione (Forgiatore)
        slot_scelto: slot,
        target_id: targetId   // NUOVO: Chi riceve l'innesto (Destinatario)
      })
    }
  );
}

/**
 * Crea una richiesta di operazione chirurgica a un altro giocatore.
 */
export const richiediOperazioneChirurgica = (forgiaturaId, slot, medicoNome, offerta, characterId) => {
  return fetchAuthenticated(
    '/api/personaggi/api/richieste-assemblaggio/crea/', 
    {
      method: 'POST',
      body: JSON.stringify({
        committente_id: characterId,
        forgiatura_id: forgiaturaId,
        slot_destinazione: slot,
        artigiano_nome: medicoNome,
        offerta: offerta,
        tipo_operazione: 'GRAF' // Codice per Graft/Innesto
      })
    }
  );
};

export const richiediAssemblaggio = (data) => {
  return fetchAuthenticated(
    '/api/personaggi/api/richieste-assemblaggio/crea/', 
    {
      method: 'POST',
      body: JSON.stringify(data)
    }
  );
};

/**
 * Rifiuta una richiesta (Wrapper per coerenza di naming).
 */
export const rifiutaRichiestaAssemblaggio = (requestId) => {
  return fetchAuthenticated(
    `/api/personaggi/api/richieste-assemblaggio/${requestId}/rifiuta/`, 
    { method: 'POST' }
  );
};

/**
 * Aggiorna parzialmente i dati del personaggio (es. impostazioni UI).
 */
export const updatePersonaggioSettings = (charId, settingsData, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/personaggi/${charId}/`, 
    {
      method: 'PATCH',
      body: JSON.stringify({ impostazioni_ui: settingsData })
    }, 
    onLogout
  );
};

// --- FUNZIONI CERIMONIALI ---

export const getAcquirableCerimoniali = (charId, onLogout) => {
    return fetchAuthenticated(
        `/api/personaggi/api/personaggio/me/cerimoniali_acquistabili/?char_id=${charId}`, 
        { method: 'GET' }, 
        onLogout
    );
}

// Modifica HP di un mostro in tempo reale
export const updateMostroHp = (mostroId, delta, onLogout) => {
  return fetchAuthenticated(
    `/api/plot/api/mostri-istanza/${mostroId}/modifica_hp/`,
    {
      method: 'POST',
      body: JSON.stringify({ delta })
    },
    onLogout
  );
};

// Associa un QR code scansionato a un oggetto "Vista" della quest
export const associaQrAVista = (vistaId, qrId, onLogout, force = false) => {
  return fetchAuthenticated(
    `/api/plot/api/viste-setup/${vistaId}/associa_qr/`,
    {
      method: 'POST',
      body: JSON.stringify({ qr_id: qrId, force: force })
    },
    onLogout
  );
};

// Associa QR direttamente a un elemento derivato da A_vista (Tessitura, Infusione, Cerimoniale, Oggetto, OggettoBase, Inventario)
export const associaQrDiretto = (aVistaId, qrId, onLogout, force = false, usiMax = undefined) => {
  const body = { qr_id: qrId, force: force };
  // undefined = non inviare (lascia invariato); null = illimitato; numero = limite
  if (usiMax !== undefined) {
    body.usi_max = usiMax;
  }
  return fetchAuthenticated(
    `/api/personaggi/api/a-vista/${aVistaId}/associa-qr/`,
    {
      method: 'POST',
      body: JSON.stringify(body)
    },
    onLogout
  );
};

/** Prelievo diretto da inventario QR non-PG (oggetto / consumabile / crediti). */
export const prendiDaInventarioQr = (payload, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/inventario-qr/prendi/',
    { method: 'POST', body: JSON.stringify(payload) },
    onLogout,
  );

// --- EVENTI ---
export const getEventi = (onLogout) => fetchAuthenticated('/api/plot/api/eventi/', { method: 'GET' }, onLogout);
/** Solo id+titolo — per select staff (Tasks), senza il payload plot completo. */
export const getEventiOpzioni = (onLogout) =>
  fetchAuthenticated('/api/plot/api/eventi/opzioni/', { method: 'GET' }, onLogout);
export const createEvento = (data, onLogout) => fetchAuthenticated('/api/plot/api/eventi/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const updateEvento = (id, data, onLogout) => fetchAuthenticated(`/api/plot/api/eventi/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const deleteEvento = (id, onLogout) => fetchAuthenticated(`/api/plot/api/eventi/${id}/`, { method: 'DELETE' }, onLogout);
export const archiviaEvento = (id, onLogout) =>
  fetchAuthenticated(`/api/plot/api/eventi/${id}/archivia/`, { method: 'POST', body: '{}' }, onLogout);
export const ripristinaEvento = (id, onLogout) =>
  fetchAuthenticated(`/api/plot/api/eventi/${id}/ripristina/`, { method: 'POST', body: '{}' }, onLogout);
/**
 * Avvio evento. `riassegnaPremi` omesso = prima chiamata: se qualcuno ha già incassato
 * il premio il backend risponde 409 `premi_gia_assegnati` e lo staff deve scegliere.
 */
export const iniziaEvento = (id, onLogout, { riassegnaPremi = null } = {}) =>
  fetchAuthenticated(
    `/api/plot/api/eventi/${id}/inizia/`,
    {
      method: 'POST',
      body: JSON.stringify(riassegnaPremi === null ? {} : { riassegna_premi: !!riassegnaPremi }),
    },
    onLogout,
  );
/** Chiusura evento: `force` serve per confermare la chiusura di un evento appena avviato. */
export const terminaEvento = (id, onLogout, { force = false } = {}) =>
  fetchAuthenticated(
    `/api/plot/api/eventi/${id}/termina/`,
    { method: 'POST', body: JSON.stringify({ force: !!force }) },
    onLogout,
  );
export const riallineaIscrizioniEvento = (id, onLogout) =>
  fetchAuthenticated(`/api/plot/api/eventi/${id}/riallinea_iscrizioni/`, { method: 'POST', body: '{}' }, onLogout);
export const reportRicompenseEvento = (id, onLogout) =>
  fetchAuthenticated(`/api/plot/api/eventi/${id}/report_ricompense/`, { method: 'GET' }, onLogout);
export const riassegnaPremiMancantiEvento = (id, onLogout) =>
  fetchAuthenticated(`/api/plot/api/eventi/${id}/riassegna_premi_mancanti/`, { method: 'POST', body: '{}' }, onLogout);

// --- TASK / MISSIONI ---
export const getMissioni = (params = {}, onLogout) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const suffix = qs.toString() ? `?${qs}` : '';
  return fetchAuthenticated(`/api/plot/api/missioni/${suffix}`, { method: 'GET' }, onLogout);
};
export const getMissione = (id, onLogout) =>
  fetchAuthenticated(`/api/plot/api/missioni/${id}/`, { method: 'GET' }, onLogout);
export const createMissione = (data, onLogout) =>
  fetchAuthenticated('/api/plot/api/missioni/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const updateMissione = (id, data, onLogout) =>
  fetchAuthenticated(`/api/plot/api/missioni/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const deleteMissione = (id, onLogout) =>
  fetchAuthenticated(`/api/plot/api/missioni/${id}/`, { method: 'DELETE' }, onLogout);
export const getMissioniMie = (personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/missioni/mie/?personaggio=${encodeURIComponent(personaggioId)}`,
    { method: 'GET' },
    onLogout,
  );
export const proponiSoluzioneMissione = (data, onLogout) =>
  fetchAuthenticated('/api/plot/api/missioni/proponi-soluzione/', {
    method: 'POST',
    body: JSON.stringify(data),
  }, onLogout);
export const getMissioniProposte = (params = {}, onLogout) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const suffix = qs.toString() ? `?${qs}` : '';
  return fetchAuthenticated(`/api/plot/api/missioni/proposte/${suffix}`, { method: 'GET' }, onLogout);
};
export const accettaMissioneProposta = (propostaId, onLogout) =>
  fetchAuthenticated('/api/plot/api/missioni/accetta-proposta/', {
    method: 'POST',
    body: JSON.stringify({ proposta_id: propostaId }),
  }, onLogout);
export const rifiutaMissioneProposta = (propostaId, messaggio, onLogout) =>
  fetchAuthenticated('/api/plot/api/missioni/rifiuta-proposta/', {
    method: 'POST',
    body: JSON.stringify({ proposta_id: propostaId, messaggio }),
  }, onLogout);
/** Stato evento ufficialmente in corso (Inizia senza Termina) — per visibilità tab Tasks. */
export const getMissioniEventoAttivo = (personaggioId, onLogout) => {
  const qs = personaggioId
    ? `?personaggio=${encodeURIComponent(personaggioId)}`
    : '';
  return fetchAuthenticated(`/api/plot/api/missioni/evento-attivo/${qs}`, { method: 'GET' }, onLogout);
};
export const getMissioniEventoTasks = (eventoId, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/missioni/evento-tasks/?evento=${encodeURIComponent(eventoId)}`,
    { method: 'GET' },
    onLogout,
  );
/** PG iscritti a un evento (anche chiuso), per accreditare una task a mano. */
export const getMissioniEventoPartecipanti = (eventoId, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/missioni/evento-partecipanti/?evento=${encodeURIComponent(eventoId)}`,
    { method: 'GET' },
    onLogout,
  );
export const setMissioneAttivaEvento = (missioneId, eventoId, attiva, onLogout) =>
  fetchAuthenticated(`/api/plot/api/missioni/${missioneId}/set-attiva-evento/`, {
    method: 'POST',
    body: JSON.stringify({ evento_id: Number(eventoId), attiva: !!attiva }),
  }, onLogout);
export const assegnaMissioneRisoluzione = (data, onLogout) =>
  fetchAuthenticated('/api/plot/api/missioni/assegna-risoluzione/', {
    method: 'POST',
    body: JSON.stringify(data),
  }, onLogout);

export const createVocePortare = (data, onLogout) =>
  fetchAuthenticated('/api/plot/api/voci-portare/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const updateVocePortare = (id, data, onLogout) =>
  fetchAuthenticated(`/api/plot/api/voci-portare/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const deleteVocePortare = (id, onLogout) =>
  fetchAuthenticated(`/api/plot/api/voci-portare/${id}/`, { method: 'DELETE' }, onLogout);

/** Elenco eventi con iscrizione aperta, requisiti utente e meta PayPal (client id per SDK). */
export const getIscrizioniEventoEligibility = (onLogout) =>
  fetchAuthenticated('/api/plot/iscrizioni-evento/eligibility/', { method: 'GET' }, onLogout);

/** Body: { evento_id, personaggio_id, tipo?, opzione_sync_ids? } → { paypal_order_id, sandbox } */
export const postIscrizioneEventoCreaOrdine = (body, onLogout) =>
  fetchAuthenticated('/api/plot/iscrizioni-evento/crea-ordine/', {
    method: 'POST',
    body: JSON.stringify(body),
  }, onLogout);

/** Body: { paypal_order_id } → conferma iscrizione dopo approve PayPal */
export const postIscrizioneEventoCattura = (body, onLogout) =>
  fetchAuthenticated('/api/plot/iscrizioni-evento/cattura/', {
    method: 'POST',
    body: JSON.stringify(body),
  }, onLogout);

/** Body: { paypal_order_id } -> marca tentativo annullato */
export const postIscrizioneEventoAnnulla = (body, onLogout) =>
  fetchAuthenticated('/api/plot/iscrizioni-evento/annulla/', {
    method: 'POST',
    body: JSON.stringify(body),
  }, onLogout);

/** POST idempotente: accredita PC/crediti d'evento se siamo in giornata evento (una tantum per PG/evento). */
export const postEventoPremiApplica = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/evento-premi/applica/', { method: 'POST', body: '{}' }, onLogout);

export const getGiocoEventoStato = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/gioco/evento-stato/', { method: 'GET' }, onLogout);

// --- NEGOZI MERCANTE ---
export const fetchNegoziCorporativi = (charId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/negozi-mercante/corporativi/?char_id=${charId}`,
    { method: 'GET' },
    onLogout,
  );

export const fetchNegozioMercanteListino = (negozioId, charId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/negozi-mercante/${negozioId}/listino/?char_id=${charId}`,
    { method: 'GET' },
    onLogout,
  );

export const acquistaNegozioMercante = (negozioId, body, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/negozi-mercante/${negozioId}/acquista/`,
    { method: 'POST', body: JSON.stringify(body) },
    onLogout,
  );

export const vendiOggettoNegozioMercante = (negozioId, charId, oggettoId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/negozi-mercante/${negozioId}/vendi/`,
    {
      method: 'POST',
      body: JSON.stringify({ char_id: charId, oggetto_id: oggettoId }),
    },
    onLogout,
  );

export const restituisciPrestitoNegozioMercante = (
  negozioId,
  charId,
  { oggettoId = null, prestitoId = null } = {},
  onLogout,
) =>
  fetchAuthenticated(
    `/api/personaggi/api/negozi-mercante/${negozioId}/restituisci/`,
    {
      method: 'POST',
      body: JSON.stringify({
        char_id: charId,
        oggetto_id: oggettoId,
        prestito_id: prestitoId,
      }),
    },
    onLogout,
  );

export const previewVenditaNegozioMercante = (negozioId, charId, oggettoId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/negozi-mercante/${negozioId}/preview-vendita/?char_id=${charId}&oggetto_id=${oggettoId}`,
    { method: 'GET' },
    onLogout,
  );

export const staffAssociaQrNegozioMercante = (negozioId, qrId, onLogout, force = false) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${negozioId}/associa-qr/`,
    {
      method: 'POST',
      body: JSON.stringify({ qr_id: qrId, force }),
    },
    onLogout,
  );

export const staffScollegaQrNegozioMercante = (negozioId, onLogout) =>
  staffAssociaQrNegozioMercante(negozioId, null, onLogout);

export const staffGetNegoziMercante = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/negozi-mercante/', { method: 'GET' }, onLogout);

export const staffCreateNegozioMercante = (data, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/negozi-mercante/',
    { method: 'POST', body: JSON.stringify(data) },
    onLogout,
  );

export const staffUpdateNegozioMercante = (id, data, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${id}/`,
    { method: 'PATCH', body: JSON.stringify(data) },
    onLogout,
  );

export const staffDeleteNegozioMercante = (id, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${id}/`,
    { method: 'DELETE' },
    onLogout,
  );

export const staffGetNegozioMercanteVoci = (negozioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante-voci/?negozio=${negozioId}`,
    { method: 'GET' },
    onLogout,
  );

export const staffCreateNegozioMercanteVoce = (data, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/negozi-mercante-voci/',
    { method: 'POST', body: JSON.stringify(data) },
    onLogout,
  );

export const staffUpdateNegozioMercanteVoce = (id, data, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante-voci/${id}/`,
    { method: 'PATCH', body: JSON.stringify(data) },
    onLogout,
  );

export const staffDeleteNegozioMercanteVoce = (id, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante-voci/${id}/`,
    { method: 'DELETE' },
    onLogout,
  );

export const staffGetNegozioMercanteBundle = (negozioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante-bundle/?negozio=${negozioId}`,
    { method: 'GET' },
    onLogout,
  );

export const staffCreateNegozioMercanteBundle = (data, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/negozi-mercante-bundle/',
    { method: 'POST', body: JSON.stringify(data) },
    onLogout,
  );

export const staffUpdateNegozioMercanteBundle = (id, data, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante-bundle/${id}/`,
    { method: 'PATCH', body: JSON.stringify(data) },
    onLogout,
  );

export const staffDeleteNegozioMercanteBundle = (id, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante-bundle/${id}/`,
    { method: 'DELETE' },
    onLogout,
  );

export const staffGetNegozioMercanteReadiness = (negozioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${negozioId}/readiness/`,
    { method: 'GET' },
    onLogout,
  );

export const staffGetNegozioMercanteAnteprima = (negozioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${negozioId}/anteprima/`,
    { method: 'GET' },
    onLogout,
  );

export const staffGetNegozioMercanteMovimenti = (negozioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${negozioId}/movimenti/`,
    { method: 'GET' },
    onLogout,
  );

export const staffGetNegozioMercantePrestiti = (negozioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${negozioId}/prestiti/`,
    { method: 'GET' },
    onLogout,
  );

export const staffRestituisciPrestitoNegozio = (negozioId, prestitoId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${negozioId}/restituisci-prestito/`,
    {
      method: 'POST',
      body: JSON.stringify({ prestito_id: prestitoId }),
    },
    onLogout,
  );

export const staffRestituisciTuttiPrestitiNegozio = (negozioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/negozi-mercante/${negozioId}/restituisci-tutti-prestiti/`,
    { method: 'POST', body: JSON.stringify({}) },
    onLogout,
  );

// --- GIORNI ---
export const createGiorno = (data, onLogout) => fetchAuthenticated('/api/plot/api/giorni/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const updateGiorno = (id, data, onLogout) => fetchAuthenticated(`/api/plot/api/giorni/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const deleteGiorno = (id, onLogout) => fetchAuthenticated(`/api/plot/api/giorni/${id}/`, { method: 'DELETE' }, onLogout);

// --- QUESTS ---
export const createQuest = (data, onLogout) => fetchAuthenticated('/api/plot/api/quests/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const updateQuest = (id, data, onLogout) => fetchAuthenticated(`/api/plot/api/quests/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const deleteQuest = (id, onLogout) => fetchAuthenticated(`/api/plot/api/quests/${id}/`, { method: 'DELETE' }, onLogout);

// Recupera tutte le liste per i dropdown dell'editor
export const getRisorseEditor = (onLogout) => fetchAuthenticated('/api/plot/api/eventi/risorse_editor/', { method: 'GET' }, onLogout);
export const getAVistaDisponibili = (onLogout) => fetchAuthenticated('/api/plot/api/eventi/a_vista_disponibili/', { method: 'GET' }, onLogout);

// Operazioni su PnG, Mostri e Viste
// export const addPngToQuest = (data, onLogout) => fetchAuthenticated('/api/plot/api/png-assegnati/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
// export const addMostroToQuest = (data, onLogout) => fetchAuthenticated('/api/plot/api/mostri-istanza/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
// export const addVistaToQuest = (data, onLogout) => fetchAuthenticated('/api/plot/api/viste-setup/', { method: 'POST', body: JSON.stringify(data) }, onLogout);

export const removePngFromQuest = (id, onLogout) => fetchAuthenticated(`/api/plot/api/png-assegnati/${id}/`, { method: 'DELETE' }, onLogout);
export const removeMostroFromQuest = (id, onLogout) => fetchAuthenticated(`/api/plot/api/mostri-istanza/${id}/`, { method: 'DELETE' }, onLogout);
export const removeVistaFromQuest = (id, onLogout) => fetchAuthenticated(`/api/plot/api/viste-setup/${id}/`, { method: 'DELETE' }, onLogout);

export const addPngToQuest = (questId, personaggioId, stafferId, onLogout) => {
    const payload = {
        quest: parseInt(questId),
        personaggio: parseInt(personaggioId),
        staffer: stafferId ? parseInt(stafferId) : null
    };
    return fetchAuthenticated('/api/plot/api/png-assegnati/', { method: 'POST', body: JSON.stringify(payload) }, onLogout);
};

// Correzione Mostri
export const addMostroToQuest = (questId, templateId, stafferId, onLogout) => {
    const payload = {
        quest: parseInt(questId),
        template: parseInt(templateId),
        staffer: stafferId ? parseInt(stafferId) : null
    };
    return fetchAuthenticated('/api/plot/api/mostri-istanza/', { method: 'POST', body: JSON.stringify(payload) }, onLogout);
};

// Correzione Viste: accetta l'oggetto vistaPayload
export const addVistaToQuest = (questId, data, onLogout) => {
    const payload = {
        quest: parseInt(questId),
        tipo: data.tipo,
        a_vista_id: data.tipo === 'NEG' ? data.a_vista_id : parseInt(data.a_vista_id, 10),
    };
    return fetchAuthenticated('/api/plot/api/viste-setup/', { method: 'POST', body: JSON.stringify(payload) }, onLogout);
};

/** --- STRUMENTI MASTER --- **/

// Recupera la lista di tutte le statistiche definite nel sistema
export const getStatisticheList = (onLogout) => {
  return fetchAuthenticated('/api/personaggi/api/statistiche/', { method: 'GET' }, onLogout);
};

export const staffGetFormulaBuilderSchema = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/formula-builder/schema/', { method: 'GET' }, onLogout);

export const staffPreviewFormulaBuilder = (payload, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/formula-builder/preview/',
    { method: 'POST', body: JSON.stringify(payload || {}) },
    onLogout
  );

export const staffGetFormulaSemanticOptions = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/formula-semantic-options/', { method: 'GET' }, onLogout);

// --- INFUSIONI ---
export const staffGetInfusioni = (onLogout, params = {}) => {
  // Costruiamo la query string (es: ?page=1&search=pippo)
  const queryString = new URLSearchParams(params).toString();
  return fetchAuthenticated(`/api/personaggi/api/staff/infusioni/?${queryString}`, { method: 'GET' }, onLogout);
};

export const staffCreateInfusione = (data, onLogout) => 
  fetchAuthenticated('/api/personaggi/api/staff/infusioni/', { method: 'POST', body: JSON.stringify(data) }, onLogout);

export const staffUpdateInfusione = (id, data, onLogout) => 
  fetchAuthenticated(`/api/personaggi/api/staff/infusioni/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);

export const staffGetInfusioneDetail = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/infusioni/${id}/`, { method: 'GET' }, onLogout);

export const staffDeleteInfusione = (id, onLogout) => 
  fetchAuthenticated(`/api/personaggi/api/staff/infusioni/${id}/`, { method: 'DELETE' }, onLogout);

/** Staff: crea un'istanza dall'infusione. personaggio_id è opzionale. */
export const staffCreaIstanzaDaInfusione = (infusioneId, payload, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/infusioni/${infusioneId}/crea-istanza/`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  }, onLogout);

// --- TESSITURE ---
export const staffGetTessiture = (onLogout, params = {}) => {
  // Costruiamo la query string (es: ?page=1&search=pippo)
  const queryString = new URLSearchParams(params).toString();
  return fetchAuthenticated(`/api/personaggi/api/staff/tessiture/?${queryString}`, { method: 'GET' }, onLogout);
};

export const staffCreateTessitura = (data, onLogout) => 
  fetchAuthenticated('/api/personaggi/api/staff/tessiture/', { method: 'POST', body: JSON.stringify(data) }, onLogout);

export const staffUpdateTessitura = (id, data, onLogout) => 
  fetchAuthenticated(`/api/personaggi/api/staff/tessiture/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);

export const staffGetTessituraDetail = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/tessiture/${id}/`, { method: 'GET' }, onLogout);

export const staffDeleteTessitura = (id, onLogout) => 
  fetchAuthenticated(`/api/personaggi/api/staff/tessiture/${id}/`, { method: 'DELETE' }, onLogout);

// --- CERIMONIALI ---
export const staffGetCerimoniali = (onLogout, params = {}) => {
  // Costruiamo la query string (es: ?page=1&search=pippo)
  const queryString = new URLSearchParams(params).toString();
  return fetchAuthenticated(`/api/personaggi/api/staff/cerimoniali/?${queryString}`, { method: 'GET' }, onLogout);
};

export const staffCreateCerimoniale = (data, onLogout) => 
  fetchAuthenticated('/api/personaggi/api/staff/cerimoniali/', { method: 'POST', body: JSON.stringify(data) }, onLogout);

export const staffUpdateCerimoniale = (id, data, onLogout) => 
  fetchAuthenticated(`/api/personaggi/api/staff/cerimoniali/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);

export const staffGetCerimonialeDetail = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/cerimoniali/${id}/`, { method: 'GET' }, onLogout);

export const staffDeleteCerimoniale = (id, onLogout) => 
  fetchAuthenticated(`/api/personaggi/api/staff/cerimoniali/${id}/`, { method: 'DELETE' }, onLogout);

// Gestione Oggetti (Istanze)
export const staffGetOggetti = (onLogout) => 
    fetchAuthenticated('/api/personaggi/api/staff/oggetti/', { method: 'GET' }, onLogout);

export const staffCreateOggetto = (data, onLogout) => 
    fetchAuthenticated('/api/personaggi/api/staff/oggetti/', { method: 'POST', body: JSON.stringify(data) }, onLogout);

export const staffUpdateOggetto = (id, data, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/oggetti/${id}/`, { method: 'PUT', body: JSON.stringify(data) }, onLogout);

export const staffDeleteOggetto = (id, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/oggetti/${id}/`, { method: 'DELETE' }, onLogout);

// Gestione Oggetti Base (Listino/Template)
export const staffGetOggettiBase = (onLogout) => 
    fetchAuthenticated('/api/personaggi/api/staff/oggetti-base/', { method: 'GET' }, onLogout);

export const staffCreateOggettoBase = (data, onLogout) => 
    fetchAuthenticated('/api/personaggi/api/staff/oggetti-base/', { method: 'POST', body: JSON.stringify(data) }, onLogout);

export const staffUpdateOggettoBase = (id, data, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/oggetti-base/${id}/`, { method: 'PUT', body: JSON.stringify(data) }, onLogout);

export const staffDeleteOggettoBase = (id, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/oggetti-base/${id}/`, { method: 'DELETE' }, onLogout);

export const staffPropagaOggettoBaseIstanze = (id, { dryRun = false } = {}, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/staff/oggetti-base/${id}/propaga-istanze/`,
        { method: 'POST', body: JSON.stringify({ dry_run: dryRun }) },
        onLogout
    );

// Utility
export const staffGetClassiOggetto = (onLogout) => 
    fetchAuthenticated('/api/personaggi/api/staff/classi-oggetto/', { method: 'GET' }, onLogout);

// --- GESTIONE INVENTARI (Staff) ---
export const staffGetInventari = (onLogout) => 
    fetchAuthenticated('/api/personaggi/api/staff/inventari/', { method: 'GET' }, onLogout);

export const staffCreateInventario = (data, onLogout) => 
    fetchAuthenticated('/api/personaggi/api/staff/inventari/', { method: 'POST', body: JSON.stringify(data) }, onLogout);

export const staffUpdateInventario = (id, data, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/inventari/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);

export const staffDeleteInventario = (id, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/inventari/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetInventarioOggetti = (inventarioId, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/inventari/${inventarioId}/oggetti/`, { method: 'GET' }, onLogout);

export const staffGetManifesti = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/manifesti/', { method: 'GET' }, onLogout);
export const staffCreateManifesto = (data, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/manifesti/',
    { method: 'POST', body: data instanceof FormData ? data : JSON.stringify(data) },
    onLogout,
  );
export const staffUpdateManifesto = (id, data, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/manifesti/${id}/`,
    { method: 'PATCH', body: data instanceof FormData ? data : JSON.stringify(data) },
    onLogout,
  );
export const staffDeleteManifesto = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/manifesti/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetNodi = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/nodi/', { method: 'GET' }, onLogout);
export const staffGetNodoRewardConfigs = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/nodi-reward-config/', { method: 'GET' }, onLogout);
export const staffCreateNodo = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/nodi/', { method: 'POST', body: data }, onLogout);
export const staffUpdateNodo = (id, data, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/nodi/${id}/`, { method: 'PATCH', body: data }, onLogout);
export const staffDeleteNodo = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/nodi/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetInnescoTimers = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/innesco-timer/', { method: 'GET' }, onLogout);
export const staffCreateInnescoTimer = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/innesco-timer/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdateInnescoTimer = (id, data, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/innesco-timer/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeleteInnescoTimer = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/innesco-timer/${id}/`, { method: 'DELETE' }, onLogout);
export const staffInnescoTimerEventi = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/innesco-timer/eventi-opzioni/', { method: 'GET' }, onLogout);
export const staffAggiungiIstanzeInnescoTimer = (id, quante, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/innesco-timer/${id}/aggiungi-istanze/`, {
    method: 'POST',
    body: JSON.stringify({ quante }),
  }, onLogout);
export const staffDeltaCaricheInnescoTimer = (id, delta, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/innesco-timer/${id}/cariche/`, {
    method: 'POST',
    body: JSON.stringify({ delta }),
  }, onLogout);
export const ackInnescoTimerScaduto = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/timers/active/ack/', {
    method: 'POST',
    body: JSON.stringify(data),
  }, onLogout);

// --- Pool QR randomico / Trappola / Serie ---
export const staffGetRandomQrPools = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/random-qr-pools/', { method: 'GET' }, onLogout);
export const staffCreateRandomQrPool = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/random-qr-pools/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdateRandomQrPool = (id, data, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/random-qr-pools/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeleteRandomQrPool = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/random-qr-pools/${id}/`, { method: 'DELETE' }, onLogout);
export const staffRandomQrPoolAddQr = (poolId, qrCodeId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/random-qr-pools/${poolId}/add-qr/`,
    { method: 'POST', body: JSON.stringify({ qr_code_id: qrCodeId }) },
    onLogout,
  );
export const staffRandomQrPoolRemoveQr = (poolId, qrCodeId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/random-qr-pools/${poolId}/remove-qr/`,
    { method: 'POST', body: JSON.stringify({ qr_code_id: qrCodeId }) },
    onLogout,
  );
export const staffCreateRandomQrPoolEffect = (poolId, data, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/random-qr-pools/${poolId}/effetti/`,
    { method: 'POST', body: JSON.stringify(data) },
    onLogout,
  );
export const staffUpdateRandomQrPoolEffect = (id, data, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/random-qr-pool-effetti/${id}/`,
    { method: 'PATCH', body: JSON.stringify(data) },
    onLogout,
  );
export const staffDeleteRandomQrPoolEffect = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/random-qr-pool-effetti/${id}/`, { method: 'DELETE' }, onLogout);

// --- Pattern minigioco / default sezione ---
export const staffGetMinigiocoPatterns = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/minigioco-patterns/', { method: 'GET' }, onLogout);
export const staffCreateMinigiocoPattern = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/minigioco-patterns/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdateMinigiocoPattern = (id, data, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/minigioco-patterns/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeleteMinigiocoPattern = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/minigioco-patterns/${id}/`, { method: 'DELETE' }, onLogout);
export const staffGetMinigiocoSezioneDefault = (pageKey, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/minigioco-sezione-defaults/by-page/${encodeURIComponent(pageKey)}/`,
    { method: 'GET' },
    onLogout,
  );
export const staffSaveMinigiocoSezioneDefault = (data, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/minigioco-sezione-defaults/',
    { method: 'POST', body: JSON.stringify(data) },
    onLogout,
  );

export const staffGetSerieCollezioni = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/serie-collezioni/', { method: 'GET' }, onLogout);
export const staffCreateSerieCollezione = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/serie-collezioni/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdateSerieCollezione = (id, data, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/serie-collezioni/${id}/`,
    { method: 'PATCH', body: data instanceof FormData ? data : JSON.stringify(data) },
    onLogout,
  );
export const staffDeleteSerieCollezione = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/serie-collezioni/${id}/`, { method: 'DELETE' }, onLogout);
export const staffGetSerieStato = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/serie-collezioni/${id}/stato/`, { method: 'GET' }, onLogout);
export const staffResetSerieCollezione = (id, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/serie-collezioni/${id}/reset/`,
    { method: 'POST', body: JSON.stringify({ conferma: true }) },
    onLogout,
  );
export const getSerieInventario = (personaggioId, onLogout) => {
  const q = personaggioId != null && personaggioId !== ''
    ? `?personaggio_id=${encodeURIComponent(personaggioId)}`
    : '';
  return fetchAuthenticated(`/api/personaggi/api/serie-inventario/${q}`, { method: 'GET' }, onLogout);
};
export const trasferisciPezzoSerie = (assegnazioneId, personaggioId, destinatarioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/serie-inventario/${assegnazioneId}/trasferisci/`,
    {
      method: 'POST',
      body: JSON.stringify({
        personaggio_id: personaggioId,
        destinatario_personaggio_id: destinatarioId,
      }),
    },
    onLogout,
  );
export const eliminaPezzoSerie = (assegnazioneId, personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/serie-inventario/${assegnazioneId}/?personaggio_id=${encodeURIComponent(personaggioId)}`,
    { method: 'DELETE' },
    onLogout,
  );
export const salvaDocumentoArchivio = (data, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/archivio-documenti/salva/',
    { method: 'POST', body: JSON.stringify(data) },
    onLogout,
  );
export const trasferisciDocumentoArchivio = (documentoId, personaggioId, destinatarioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/archivio-documenti/${documentoId}/trasferisci/`,
    {
      method: 'POST',
      body: JSON.stringify({
        personaggio_id: personaggioId,
        destinatario_personaggio_id: destinatarioId,
      }),
    },
    onLogout,
  );
export const eliminaDocumentoArchivio = (documentoId, personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/archivio-documenti/${documentoId}/?personaggio_id=${encodeURIComponent(personaggioId)}`,
    { method: 'DELETE' },
    onLogout,
  );
export const staffUploadSerieImmagini = (serieId, formData, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/serie-collezioni/${serieId}/immagini/`,
    { method: 'POST', body: formData },
    onLogout,
  );
export const staffDeleteSerieImmagine = (serieId, imgId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/serie-collezioni/${serieId}/immagini/${imgId}/`,
    { method: 'DELETE' },
    onLogout,
  );

export const staffGetTrappole = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/trappole/', { method: 'GET' }, onLogout);
export const staffCreateTrappola = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/trappole/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdateTrappola = (id, data, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/trappole/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeleteTrappola = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/trappole/${id}/`, { method: 'DELETE' }, onLogout);
export const staffAssociaQrTrappola = (trappolaId, qrId, onLogout, force = false) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/trappole/${trappolaId}/associa-qr/`,
    { method: 'POST', body: JSON.stringify({ qr_id: qrId, force }) },
    onLogout,
  );

export const staffGetQrCredito = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/qr-credito/', { method: 'GET' }, onLogout);
export const staffCreateQrCredito = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/qr-credito/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdateQrCredito = (id, data, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/qr-credito/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeleteQrCredito = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/qr-credito/${id}/`, { method: 'DELETE' }, onLogout);
export const staffAssociaQrCredito = (creditoId, qrId, onLogout, force = false, usiMax = undefined) => {
  const body = { qr_id: qrId, force };
  if (usiMax !== undefined) body.usi_max = usiMax;
  return fetchAuthenticated(
    `/api/personaggi/api/staff/qr-credito/${creditoId}/associa-qr/`,
    { method: 'POST', body: JSON.stringify(body) },
    onLogout,
  );
};

export const staffGetSerieQr = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/serie-qr/', { method: 'GET' }, onLogout);
export const staffCreateSerieQr = (data, onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/serie-qr/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdateSerieQr = (id, data, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/serie-qr/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeleteSerieQr = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/serie-qr/${id}/`, { method: 'DELETE' }, onLogout);
export const staffAssociaQrSerieQr = (serieQrId, qrId, onLogout, force = false) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/serie-qr/${serieQrId}/associa-qr/`,
    { method: 'POST', body: JSON.stringify({ qr_id: qrId, force }) },
    onLogout,
  );

export const staffAggiungiOggettoInventario = (inventarioId, oggettoId, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/inventari/${inventarioId}/aggiungi_oggetto/`, { 
        method: 'POST', 
        body: JSON.stringify({ oggetto_id: oggettoId }) 
    }, onLogout);

export const staffRimuoviOggettoInventario = (inventarioId, oggettoId, onLogout) => 
    fetchAuthenticated(`/api/personaggi/api/staff/inventari/${inventarioId}/rimuovi_oggetto/`, { 
        method: 'POST', 
        body: JSON.stringify({ oggetto_id: oggettoId }) 
    }, onLogout);

export const staffGetInventarioConsumabili = (inventarioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/inventari/${inventarioId}/consumabili/`,
    { method: 'GET' },
    onLogout,
  );

export const staffAggiungiConsumabileInventario = (inventarioId, data, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/inventari/${inventarioId}/aggiungi-consumabile/`,
    { method: 'POST', body: JSON.stringify(data) },
    onLogout,
  );

export const staffRimuoviConsumabileInventario = (inventarioId, consumabileId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/inventari/${inventarioId}/rimuovi-consumabile/`,
    { method: 'POST', body: JSON.stringify({ consumabile_id: consumabileId }) },
    onLogout,
  );

export const staffCreaOggettoDaInfusioneInventario = (inventarioId, infusioneId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/inventari/${inventarioId}/crea-da-infusione/`,
    { method: 'POST', body: JSON.stringify({ infusione_id: infusioneId }) },
    onLogout,
  );

export const staffCreaOggettoDaBaseInventario = (inventarioId, oggettoBaseId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/inventari/${inventarioId}/crea-da-oggetto-base/`,
    { method: 'POST', body: JSON.stringify({ oggetto_base_id: oggettoBaseId }) },
    onLogout,
  );

export const staffGetOggettiSenzaPosizione = (onLogout) => 
    fetchAuthenticated('/api/personaggi/api/staff/oggetti-senza-posizione/', { method: 'GET' }, onLogout);

// Sezione Personaggi

/**
 * Recupera le tipologie di personaggio disponibili.
 * Tenta di scaricarle dal server. Se fallisce o l'endpoint non esiste,
 * usa i valori di fallback per non rompere l'interfaccia.
 */
export const getTipologiePersonaggio = async (onLogout) => {
    try {
        // Tenta la chiamata all'API reale
        // Assicurati che nel backend esista: router.register(r'tipologie', TipologiaPersonaggioViewSet)
        const data = await fetchAuthenticated('/api/personaggi/api/tipologiepersonaggio/', { method: 'GET' }, onLogout);
        return data;
    } catch (error) {
        console.warn("API Tipologie non raggiungibile, uso fallback locale:", error);
        
        // Fallback locale nel caso il backend non sia ancora pronto
        return [
            { id: 1, label: 'Standard (Giocatore)' },
            { id: 2, label: 'PnG (Personaggio Non Giocante)' }
        ];
    }
};

// --- CRUD PERSONAGGI (Nuovo Endpoint) ---

export const getPersonaggiEditList = (onLogout, options = {}) => {
    const mineOnly = options?.mineOnly === true;
    const qs = mineOnly ? '?mine=1' : '';
    return fetchAuthenticated(`/api/personaggi/api/gestione-personaggi/${qs}`, { method: 'GET' }, onLogout);
};

export const getEre = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/ere/', { method: 'GET' }, onLogout);
};

export const staffGetEre = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/ere/', { method: 'GET' }, onLogout);
};

export const staffCreateEra = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/ere/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffUpdateEra = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/ere/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffDeleteEra = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/ere/${id}/`, {
        method: 'DELETE',
    }, onLogout);
};

export const staffGetPrefetture = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/prefetture/', { method: 'GET' }, onLogout);
};

export const staffGetRegioni = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/regioni/', { method: 'GET' }, onLogout);
};

// --- CARRIERE / KORP (STAFF) ---
export const staffGetTipiCarriera = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/tipi-carriera/', { method: 'GET' }, onLogout);

export const staffGetCarriere = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/carriere/', { method: 'GET' }, onLogout);

export const staffCreateCarriera = (data, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/carriere/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCarriera = (id, data, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/carriere/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCarriera = (id, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/carriere/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCarrieraTiersSelezionabili = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/carriere/tiers_selezionabili/', { method: 'GET' }, onLogout);

export const staffGetCariche = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/cariche/', { method: 'GET' }, onLogout);

export const staffCreateCarica = (data, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/cariche/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCarica = (id, data, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/cariche/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCarica = (id, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/cariche/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCarriereMemberships = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/personaggi-carriere-membership/', { method: 'GET' }, onLogout);

export const staffCreateCarriereMembership = (data, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/personaggi-carriere-membership/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCarriereMembership = (id, data, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/personaggi-carriere-membership/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCarriereMembership = (id, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/personaggi-carriere-membership/${id}/`, {
        method: 'DELETE',
    }, onLogout);

// --- PILOTAGGIO (STAFF) ---
export const staffGetPilotSottosistemi = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/sottosistemi/', { method: 'GET' }, onLogout);
/** Dettaglio completo (serializer «retrieve») per modali staff. */
export const staffGetPilotSottosistema = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/sottosistemi/${id}/`, { method: 'GET' }, onLogout);
export const staffCreatePilotSottosistema = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/sottosistemi/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdatePilotSottosistema = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/sottosistemi/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeletePilotSottosistema = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/sottosistemi/${id}/`, { method: 'DELETE' }, onLogout);
export const staffAssociaPilotSottosistemaAVista = (id, aVistaId, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/sottosistemi/${id}/associa-a-vista/`, { method: 'POST', body: JSON.stringify({ a_vista_id: aVistaId }) }, onLogout);

/** Collega un QR al sottosistema (risolve/c crea la vista lato server). */
export const staffAssociaPilotSottosistemaQr = (sottosistemaId, qrId, onLogout) =>
  fetchAuthenticated(
    `/api/pilot/staff/sottosistemi/${sottosistemaId}/associa-qr/`,
    { method: 'POST', body: JSON.stringify({ qr_id: qrId }) },
    onLogout
  );

export const staffGetPilotSerbatoioCarburante = (sottosistemaId, onLogout) =>
  fetchAuthenticated(
    `/api/pilot/staff/sottosistemi/${sottosistemaId}/carburante-sessione/`,
    { method: 'GET' },
    onLogout
  );

export const staffSetPilotSerbatoioCarburante = (sottosistemaId, data, onLogout) =>
  fetchAuthenticated(
    `/api/pilot/staff/sottosistemi/${sottosistemaId}/carburante-sessione/`,
    { method: 'POST', body: JSON.stringify(data) },
    onLogout
  );

export const staffGetPilotComandi = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/comandi/', { method: 'GET' }, onLogout);
export const staffCreatePilotComando = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/comandi/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdatePilotComando = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/comandi/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeletePilotComando = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/comandi/${id}/`, { method: 'DELETE' }, onLogout);

/** Pattern globali: codice in sessione → precipizio immediato (indipendente dall'evento). */
export const staffGetPilotComandiCritici = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/comandi-critici/', { method: 'GET' }, onLogout);
export const staffGetPilotComandoCritico = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/comandi-critici/${id}/`, { method: 'GET' }, onLogout);
export const staffCreatePilotComandoCritico = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/comandi-critici/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdatePilotComandoCritico = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/comandi-critici/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeletePilotComandoCritico = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/comandi-critici/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetPilotPercorsi = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/percorsi/', { method: 'GET' }, onLogout);
export const staffCreatePilotPercorso = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/percorsi/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdatePilotPercorso = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/percorsi/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeletePilotPercorso = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/percorsi/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetPilotDipartimentiKorp = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/dipartimenti-korp/', { method: 'GET' }, onLogout);
export const staffGetPilotProtocolli = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/protocolli-comunicazione/', { method: 'GET' }, onLogout);
export const staffCreatePilotProtocollo = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/protocolli-comunicazione/', {
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data),
  }, onLogout);
export const staffUpdatePilotProtocollo = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/protocolli-comunicazione/${id}/`, {
    method: 'PATCH',
    body: data instanceof FormData ? data : JSON.stringify(data),
  }, onLogout);
export const staffDeletePilotProtocollo = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/protocolli-comunicazione/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetPilotIntensita = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/intensita/', { method: 'GET' }, onLogout);
export const staffGetPilotIntensitaById = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/intensita/${id}/`, { method: 'GET' }, onLogout);
export const staffCreatePilotIntensita = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/intensita/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdatePilotIntensita = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/intensita/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeletePilotIntensita = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/intensita/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetPilotEventi = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/eventi/', { method: 'GET' }, onLogout);
export const staffGetPilotEvento = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/eventi/${id}/`, { method: 'GET' }, onLogout);
export const staffCreatePilotEvento = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/eventi/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdatePilotEvento = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/eventi/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeletePilotEvento = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/eventi/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetPilotSequenze = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/sequenze/', { method: 'GET' }, onLogout);

export const staffGetPilotStatiAllerta = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/stati-allerta/', { method: 'GET' }, onLogout);
export const staffGetPilotStatoAllerta = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/stati-allerta/${id}/`, { method: 'GET' }, onLogout);
export const staffUpdatePilotStatoAllerta = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/stati-allerta/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }, onLogout);
export const staffCreatePilotSequenza = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/sequenze/', { method: 'POST', body: JSON.stringify(data) }, onLogout);
export const staffUpdatePilotSequenza = (id, data, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/sequenze/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const staffDeletePilotSequenza = (id, onLogout) =>
  fetchAuthenticated(`/api/pilot/staff/sequenze/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetPilotCatalog = (onLogout) =>
  fetchAuthenticated('/api/pilot/catalog/', { method: 'GET' }, onLogout);

export const staffGetPilotRuntimeConfig = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/runtime-config/', { method: 'GET' }, onLogout);

export const getPilotNavigationConfig = (onLogout) =>
  fetchAuthenticated('/api/pilot/navigation-config/', { method: 'GET' }, onLogout);

export const staffGetPilotSessioneLive = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/sessione-live/', { method: 'GET' }, onLogout);
export const staffGetPilotSessioniOrfane = (onLogout, pilotaId = null) => {
  const params = new URLSearchParams();
  if (pilotaId != null) params.set('pilota_id', String(pilotaId));
  const q = params.toString();
  const path = q ? `/api/pilot/staff/sessioni-orfane/?${q}` : '/api/pilot/staff/sessioni-orfane/';
  return fetchAuthenticated(path, { method: 'GET' }, onLogout);
};
export const staffPulisciPilotSessioniOrfane = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/sessioni-orfane/', {
    method: 'POST',
    body: JSON.stringify(data || {}),
  }, onLogout);
export const staffAzionePilotSessioneSottosistema = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/sessione-live/sottosistema/', {
    method: 'POST',
    body: JSON.stringify(data),
  }, onLogout);
export const staffUpdatePilotRuntimeConfig = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/runtime-config/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  }, onLogout);

export const staffGetPilotStiva = (onLogout) =>
  fetchAuthenticated('/api/pilot/staff/stiva/', { method: 'GET' }, onLogout);

export const staffModificaPilotStiva = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/stiva/', {
    method: 'POST',
    body: JSON.stringify(data),
  }, onLogout);

export const getPilotStiva = (personaggioId, onLogout) => {
  const q = personaggioId ? `?personaggio_id=${encodeURIComponent(personaggioId)}` : '';
  return fetchAuthenticated(`/api/pilot/stiva/${q}`, { method: 'GET' }, onLogout);
};

export const staffAggiornaCodiciEventiPilot = (data, onLogout) =>
  fetchAuthenticated('/api/pilot/staff/eventi/aggiorna-codici-da-stato/', {
    method: 'POST',
    body: JSON.stringify(data || {}),
  }, onLogout);

export const staffGetKorps = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/korp/', { method: 'GET' }, onLogout);
};

export const staffCreateRegione = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/regioni/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffUpdateRegione = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/regioni/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffDeleteRegione = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/regioni/${id}/`, {
        method: 'DELETE',
    }, onLogout);
};

export const staffCreatePrefettura = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/prefetture/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffUpdatePrefettura = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/prefetture/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffDeletePrefettura = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/prefetture/${id}/`, {
        method: 'DELETE',
    }, onLogout);
};

export const staffGetDichiarazioni = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/dichiarazioni/', { method: 'GET' }, onLogout);
};

export const staffCreateDichiarazione = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/dichiarazioni/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffUpdateDichiarazione = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/dichiarazioni/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffDeleteDichiarazione = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/dichiarazioni/${id}/`, {
        method: 'DELETE',
    }, onLogout);
};

export const staffGetStatistiche = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/statistiche/', { method: 'GET' }, onLogout);
};

export const staffCreateStatistica = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/statistiche/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffUpdateStatistica = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/statistiche/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffDeleteStatistica = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/statistiche/${id}/`, {
        method: 'DELETE',
    }, onLogout);
};

export const staffGetAure = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/aure/', { method: 'GET' }, onLogout);
};

export const staffCreateAura = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/aure/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffUpdateAura = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/aure/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffDeleteAura = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/aure/${id}/`, {
        method: 'DELETE',
    }, onLogout);
};

const staffCatalogCrud = (path) => ({
    list: (onLogout) => fetchAuthenticated(`/api/personaggi/api/staff/${path}/`, { method: 'GET' }, onLogout),
    create: (data, onLogout) => fetchAuthenticated(`/api/personaggi/api/staff/${path}/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout),
    update: (id, data, onLogout) => fetchAuthenticated(`/api/personaggi/api/staff/${path}/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout),
    remove: (id, onLogout) => fetchAuthenticated(`/api/personaggi/api/staff/${path}/${id}/`, {
        method: 'DELETE',
    }, onLogout),
});

const caratteristicheCrud = staffCatalogCrud('caratteristiche');
export const staffGetCaratteristiche = caratteristicheCrud.list;
export const staffCreateCaratteristica = caratteristicheCrud.create;
export const staffUpdateCaratteristica = caratteristicheCrud.update;
export const staffDeleteCaratteristica = caratteristicheCrud.remove;

const mattoniCrud = staffCatalogCrud('mattoni');
export const staffGetMattoni = mattoniCrud.list;
export const staffCreateMattone = mattoniCrud.create;
export const staffUpdateMattone = mattoniCrud.update;
export const staffDeleteMattone = mattoniCrud.remove;

const modelliAuraCrud = staffCatalogCrud('modelli-aura');
export const staffGetModelliAura = modelliAuraCrud.list;
export const staffCreateModelloAura = modelliAuraCrud.create;
export const staffUpdateModelloAura = modelliAuraCrud.update;
export const staffDeleteModelloAura = modelliAuraCrud.remove;

const punteggiResiduiCrud = staffCatalogCrud('punteggi');
export const staffGetPunteggiResidui = punteggiResiduiCrud.list;
export const staffCreatePunteggioResiduo = punteggiResiduiCrud.create;
export const staffUpdatePunteggioResiduo = punteggiResiduiCrud.update;
export const staffDeletePunteggioResiduo = punteggiResiduiCrud.remove;

export const createPersonaggio = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/gestione-personaggi/', {
        method: 'POST',
        body: JSON.stringify(data)
    }, onLogout);
};

export const updatePersonaggio = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/gestione-personaggi/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data)
    }, onLogout);
};

export const getGestionePersonaggio = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/gestione-personaggi/${id}/`, { method: 'GET' }, onLogout);
};

export const rigeneraLikeInfluencer = (id, onLogout) => {
    return fetchAuthenticated(
        `/api/personaggi/api/gestione-personaggi/${id}/rigenera-like-influencer/`,
        { method: 'POST' },
        onLogout
    );
};

export const deletePersonaggio = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/gestione-personaggi/${id}/`, {
        method: 'DELETE',
    }, onLogout);
};

export const staffGetPersonaggiEliminati = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/personaggi-eliminati/', { method: 'GET' }, onLogout);

export const staffRestorePersonaggioEliminato = (id, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/personaggi-eliminati/${id}/restore/`, { method: 'POST' }, onLogout);

export const staffHardDeletePersonaggioEliminato = (id, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/staff/personaggi-eliminati/${id}/hard-delete/`,
        { method: 'POST' },
        onLogout
    );

// --- CREAZIONE GUIDATA PERSONAGGIO ---
const _creazioneGuidataQuery = (personaggioId, effetti, modalitaTest) => {
    const params = new URLSearchParams();
    if (personaggioId) params.set('personaggio_id', String(personaggioId));
    if (effetti?.length) params.set('effetti', JSON.stringify(effetti));
    if (modalitaTest) params.set('modalita_test', '1');
    return params.toString();
};

export const getCreazioneGuidataStato = (onLogout) =>
    fetchAuthenticated('/api/plot/api/creazione-guidata/stato/', { method: 'GET' }, onLogout);

export const getCreazioneGuidataImpostazioni = (onLogout) =>
    fetchAuthenticated('/api/plot/api/creazione-guidata/impostazioni/', { method: 'GET' }, onLogout);

export const patchCreazioneGuidataImpostazioni = (apertaGiocatori, onLogout) =>
    fetchAuthenticated(
        '/api/plot/api/creazione-guidata/impostazioni/',
        { method: 'PATCH', body: JSON.stringify({ aperta_giocatori: apertaGiocatori }) },
        onLogout,
    );

export const getCreazioneGuidataAvvio = (personaggioId, effetti, onLogout, modalitaTest = false) => {
    const qs = _creazioneGuidataQuery(personaggioId, effetti, modalitaTest);
    return fetchAuthenticated(
        `/api/plot/api/creazione-guidata/avvio${qs ? `?${qs}` : ''}`,
        { method: 'GET' },
        onLogout,
    );
};

export const getCreazioneGuidataPasso = (slug, personaggioId, effetti, onLogout, modalitaTest = false) => {
    const qs = _creazioneGuidataQuery(personaggioId, effetti, modalitaTest);
    return fetchAuthenticated(
        `/api/plot/api/creazione-guidata/passi/${encodeURIComponent(slug)}${qs ? `?${qs}` : ''}`,
        { method: 'GET' },
        onLogout,
    );
};

export const applyCreazioneGuidata = (personaggioId, effetti, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/gestione-personaggi/${personaggioId}/creazione-guidata/applica/`,
        { method: 'POST', body: JSON.stringify({ effetti }) },
        onLogout,
    );

export const getCreazioneGuidataProposte = (personaggioId, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/gestione-personaggi/${personaggioId}/creazione-guidata/proposte/`,
        { method: 'GET' },
        onLogout,
    );

export const salvaCreazioneGuidataProposte = (personaggioId, effetti, trail, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/gestione-personaggi/${personaggioId}/creazione-guidata/salva-proposte/`,
        { method: 'POST', body: JSON.stringify({ effetti, trail }) },
        onLogout,
    );

export const getCreazioneGuidataRiepilogo = (personaggioId, effetti, onLogout) => {
    const params = new URLSearchParams();
    if (effetti?.length) params.set('effetti', JSON.stringify(effetti));
    const qs = params.toString();
    return fetchAuthenticated(
        `/api/personaggi/api/gestione-personaggi/${personaggioId}/creazione-guidata/riepilogo${qs ? `?${qs}` : ''}`,
        { method: 'GET' },
        onLogout,
    );
};

export const listCreazioneGuidataFlussi = (onLogout) =>
    fetchAuthenticated('/api/plot/api/staff/creazione-guidata/flussi/', { method: 'GET' }, onLogout);

export const getCreazioneGuidataFlusso = (id, onLogout) =>
    fetchAuthenticated(`/api/plot/api/staff/creazione-guidata/flussi/${id}/`, { method: 'GET' }, onLogout);

export const createCreazioneGuidataFlusso = (data, onLogout) =>
    fetchAuthenticated('/api/plot/api/staff/creazione-guidata/flussi/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const creaSandboxCreazioneGuidataFlusso = (flussoProduzioneId, onLogout) =>
    fetchAuthenticated(
        `/api/plot/api/staff/creazione-guidata/flussi/${flussoProduzioneId}/crea-sandbox/`,
        { method: 'POST' },
        onLogout,
    );

export const pubblicaCreazioneGuidataSandbox = (flussoTestId, onLogout) =>
    fetchAuthenticated(
        `/api/plot/api/staff/creazione-guidata/flussi/${flussoTestId}/pubblica-su-produzione/`,
        { method: 'POST' },
        onLogout,
    );

export const updateCreazioneGuidataFlusso = (id, data, onLogout) =>
    fetchAuthenticated(`/api/plot/api/staff/creazione-guidata/flussi/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const deleteCreazioneGuidataFlusso = (id, onLogout) =>
    fetchAuthenticated(`/api/plot/api/staff/creazione-guidata/flussi/${id}/`, { method: 'DELETE' }, onLogout);

export const createCreazioneGuidataPasso = (data, onLogout) =>
    fetchAuthenticated('/api/plot/api/staff/creazione-guidata/passi/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const updateCreazioneGuidataPasso = (id, data, onLogout) =>
    fetchAuthenticated(`/api/plot/api/staff/creazione-guidata/passi/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const deleteCreazioneGuidataPasso = (id, onLogout) =>
    fetchAuthenticated(`/api/plot/api/staff/creazione-guidata/passi/${id}/`, { method: 'DELETE' }, onLogout);

export const createCreazioneGuidataScelta = (data, onLogout) =>
    fetchAuthenticated('/api/plot/api/staff/creazione-guidata/scelte/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const updateCreazioneGuidataScelta = (id, data, onLogout) =>
    fetchAuthenticated(`/api/plot/api/staff/creazione-guidata/scelte/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const deleteCreazioneGuidataScelta = (id, onLogout) =>
    fetchAuthenticated(`/api/plot/api/staff/creazione-guidata/scelte/${id}/`, { method: 'DELETE' }, onLogout);

export const resetPersonaggio = (id, reason, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/gestione-personaggi/${id}/reset_personaggio/`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || 'Reset personaggio staff' })
    }, onLogout);
};

export const staffKillPersonaggio = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/gestione-personaggi/${id}/kill/`, {
        method: 'POST',
    }, onLogout);
};

export const staffRevivePersonaggio = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/gestione-personaggi/${id}/revive/`, {
        method: 'POST',
    }, onLogout);
};

// --- STAFF RESOURCES ---
export const staffAddResources = (charId, tipo, amount, reason, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/gestione-personaggi/${charId}/add_resources/`, {
        method: 'POST',
        body: JSON.stringify({ tipo, amount, reason })
    }, onLogout);
};

/** Consuma un punto da una risorsa a pool (es. Fortuna FRT). */
export const gameConsumaRisorsa = (charId, statSigla, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/game/consuma_risorsa/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, stat_sigla: statSigla }),
    }, onLogout);

export const gameAttivaTessituraRuntime = (charId, tessituraId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/game/attiva_tessitura_runtime/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, tessitura_id: tessituraId }),
    }, onLogout);

export const gameStopTessituraRuntime = (charId, runtimeId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/game/stop_tessitura_runtime/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, runtime_id: runtimeId }),
    }, onLogout);

export const gameDisequipTessituraRuntimeObject = (charId, runtimeObjectId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/game/disequip_tessitura_runtime_object/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, runtime_object_id: runtimeObjectId }),
    }, onLogout);

export const gameComaControl = (charId, action, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/game/coma_control/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, action }),
    }, onLogout);

export const watchGetStatus = (charId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/device/watch/status/?char_id=${encodeURIComponent(String(charId))}`,
    { method: 'GET' },
    onLogout
  );

export const watchPairConfirm = (charId, code, transportMode, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/device/watch/pair/confirm/',
    {
      method: 'POST',
      body: JSON.stringify({
        char_id: charId,
        code: String(code || '').trim().toUpperCase(),
        transport_mode: transportMode || 'WIFI',
      }),
    },
    onLogout
  );

export const watchDisconnect = (charId, onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/device/watch/disconnect/',
    {
      method: 'POST',
      body: JSON.stringify({ char_id: charId }),
    },
    onLogout
  );

export const watchWearManifest = (charId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/device/watch/wearos/manifest/?char_id=${encodeURIComponent(String(charId))}`,
    { method: 'GET' },
    onLogout
  );

/** Staff: elenco personaggi con pool risorse (FRT, …). */
export const staffGetRisorsePool = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/risorse-pool/', { method: 'GET' }, onLogout);

/** Staff: lista personaggi (serializer leggero) con filtri. */
export const staffGetPersonaggi = (params = {}, onLogout) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') qs.set(k, v);
  });
  const query = qs.toString();
  return fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${query ? `?${query}` : ''}`,
    { method: 'GET' },
    onLogout,
  );
};

/** Staff: eventi selezionabili nel filtro «Iscritti all'evento» (id, titolo, date, iscritti). */
export const staffGetPersonaggiEventiOpzioni = (onLogout) =>
  fetchAuthenticated(
    '/api/personaggi/api/staff/personaggi/eventi-opzioni/',
    { method: 'GET' },
    onLogout,
  );

/** Staff: dettaglio completo personaggio per modale. */
export const staffGetPersonaggioDetail = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/`, { method: 'GET' }, onLogout);

/** Staff: patch campi personaggio (note master, BG, foto costume, …). Accetta JSON o FormData. */
export const staffPatchPersonaggio = (id, payload, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/`, {
    method: 'PATCH',
    body: payload instanceof FormData ? payload : JSON.stringify(payload),
  }, onLogout);

/** Staff: patch profilo social InstaFame di un personaggio. Accetta JSON o FormData. */
export const staffPatchPersonaggioSocialProfile = (id, payload, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/social-profile/`, {
    method: 'PATCH',
    body: payload instanceof FormData ? payload : JSON.stringify(payload),
  }, onLogout);

/** Staff: riserva scommesse — saldo e puntate recenti. */
export const staffGetPersonaggioRiservaScommesse = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/riserva-scommesse/`, { method: 'GET' }, onLogout);

/** Staff: modifica riserva scommesse ({ mode: 'delta'|'set', delta|valore, motivo }). */
export const staffAdjustPersonaggioRiservaScommesse = (id, payload, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/riserva-scommesse/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, onLogout);

/** Staff: aggiungi crediti o PC a un personaggio. */
export const staffAddResourcesToPersonaggio = (id, tipo, amount, reason, onLogout, { conto } = {}) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/add-resources/`, {
    method: 'POST',
    body: JSON.stringify({ tipo, amount, reason, ...(conto ? { conto } : {}) }),
  }, onLogout);

/** Economia duale: summary saldo / tetto trasferimento. */
export const fetchEconomiaSummary = (charId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/personaggio/me/economia/?char_id=${encodeURIComponent(charId)}`,
    { method: 'GET' },
    onLogout,
  );

export const postTrasferimentoDeposito = (charId, importo, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/personaggio/me/economia/trasferisci-deposito/`, {
    method: 'POST',
    body: JSON.stringify({ char_id: charId, importo }),
  }, onLogout);

export const fetchEconomiaMovimenti = (charId, { tipo = 'crediti', conto, limit = 40, offset = 0 } = {}, onLogout) => {
  const params = new URLSearchParams({
    char_id: String(charId),
    tipo,
    limit: String(limit),
    offset: String(offset),
  });
  if (conto) params.set('conto', conto);
  return fetchAuthenticated(
    `/api/personaggi/api/personaggio/me/economia/movimenti/?${params}`,
    { method: 'GET' },
    onLogout,
  );
};

export const fetchEconomiaConfig = (campagnaId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/economia-config/?campagna_id=${encodeURIComponent(campagnaId)}`,
    { method: 'GET' },
    onLogout,
  );

export const patchEconomiaConfig = (campagnaId, economia_config, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/economia-config/`, {
    method: 'PATCH',
    body: JSON.stringify({ campagna_id: campagnaId, economia_config }),
  }, onLogout);

export const staffGetPersonaggioEconomia = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/economia/`, { method: 'GET' }, onLogout);

export const staffTrasferisciDepositoPersonaggio = (id, payload, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/economia/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, onLogout);

/** Staff: assegna un'abilità (stesse regole e costi dell'acquisto giocatore). */
export const staffPersonaggioAssegnaAbilita = (personaggioId, abilitaId, { motivo } = {}, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/assegna-abilita/`, {
    method: 'POST',
    body: JSON.stringify({
      abilita_id: abilitaId,
      motivo: motivo || '',
    }),
  }, onLogout);

/** Staff: revoca un'abilità (validazioni giocatore + rimborso automatico). */
export const staffPersonaggioRimuoviAbilita = (personaggioId, abilitaId, { motivo } = {}, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/rimuovi-abilita/`, {
    method: 'POST',
    body: JSON.stringify({
      abilita_id: abilitaId,
      motivo: motivo || '',
    }),
  }, onLogout);

/** Staff: catalogo tecniche non possedute (infusione | tessitura | cerimoniale). */
export const staffPersonaggioCatalogoTecniche = (personaggioId, tipo, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${personaggioId}/tecniche-catalogo/?tipo=${encodeURIComponent(tipo)}`,
    { method: 'GET' },
    onLogout,
  );

/** Staff: assegna un'infusione, tessitura o cerimoniale (acquisto o omaggio). */
export const staffPersonaggioAssegnaTecnica = (
  personaggioId,
  { tipo, tecnicaId, motivo, omaggio } = {},
  onLogout,
) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/assegna-tecnica/`, {
    method: 'POST',
    body: JSON.stringify({
      tipo,
      tecnica_id: tecnicaId,
      motivo: motivo || '',
      omaggio: !!omaggio,
    }),
  }, onLogout);

/** Staff: revoca una tecnica posseduta e rimborsa i crediti pagati. */
export const staffPersonaggioRimuoviTecnica = (
  personaggioId,
  { tipo, tecnicaId, motivo } = {},
  onLogout,
) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/rimuovi-tecnica/`, {
    method: 'POST',
    body: JSON.stringify({
      tipo,
      tecnica_id: tecnicaId,
      motivo: motivo || '',
    }),
  }, onLogout);

/** Staff: assegna o sostituisce modello aura per un personaggio. */
export const staffPersonaggioAssegnaModelloAura = (personaggioId, modelloAuraId, { motivo } = {}, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/assegna-modello-aura/`, {
    method: 'POST',
    body: JSON.stringify({
      modello_aura_id: modelloAuraId,
      motivo: motivo || '',
    }),
  }, onLogout);

/** Staff: rimuove modello aura (per aura_id o modello_aura_id). */
export const staffPersonaggioRimuoviModelloAura = (personaggioId, { aura_id, modello_aura_id, motivo } = {}, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/rimuovi-modello-aura/`, {
    method: 'POST',
    body: JSON.stringify({
      aura_id,
      modello_aura_id,
      motivo: motivo || '',
    }),
  }, onLogout);

/** Staff: crea istanza Oggetto da OggettoBase nell'inventario del personaggio. */
export const staffCreaOggettoDaBasePerPersonaggio = (personaggioId, oggettoBaseId, motivo, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/crea-oggetto-da-base/`, {
    method: 'POST',
    body: JSON.stringify({ oggetto_base_id: oggettoBaseId, motivo }),
  }, onLogout);

export const staffPersonaggioAggiungiOggetto = (personaggioId, oggettoId, motivo, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/aggiungi-oggetto/`, {
    method: 'POST',
    body: JSON.stringify({ oggetto_id: oggettoId, motivo }),
  }, onLogout);

export const staffPersonaggioRimuoviOggetto = (personaggioId, oggettoId, motivo, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/rimuovi-oggetto/`, {
    method: 'POST',
    body: JSON.stringify({ oggetto_id: oggettoId, motivo }),
  }, onLogout);

export const staffPersonaggioDistruggiOggetto = (personaggioId, oggettoId, motivo, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/distruggi-oggetto/`, {
    method: 'POST',
    body: JSON.stringify({ oggetto_id: oggettoId, motivo }),
  }, onLogout);

/** Staff: istanza da infusione nell'inventario del personaggio. */
export const staffPersonaggioCreaOggettoDaInfusione = (personaggioId, payload, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/crea-oggetto-da-infusione/`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  }, onLogout);

/** Staff: monta d'ufficio materia o mod su un oggetto fisico dello stesso PG. */
export const staffPersonaggioMontaPotenziamento = (
  personaggioId,
  componenteId,
  oggettoOspiteId,
  motivo,
  onLogout,
) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/monta-potenziamento/`, {
    method: 'POST',
    body: JSON.stringify({
      componente_id: componenteId,
      oggetto_ospite_id: oggettoOspiteId,
      motivo: motivo || '',
    }),
  }, onLogout);

/** Staff: smonta d'ufficio materia o mod e la rimette in inventario. */
export const staffPersonaggioSmontaPotenziamento = (personaggioId, componenteId, motivo, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${personaggioId}/smonta-potenziamento/`, {
    method: 'POST',
    body: JSON.stringify({ componente_id: componenteId, motivo: motivo || '' }),
  }, onLogout);

/** Tessiture della campagna, per generare consumabili d'ufficio. */
export const staffPersonaggioCatalogoTessiture = (personaggioId, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${personaggioId}/catalogo-tessiture/`,
    { method: 'GET' },
    onLogout,
  );

/** Crea uno o più consumabili sul personaggio (a mano o da tessitura). */
export const staffPersonaggioAggiungiConsumabile = (personaggioId, payload, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${personaggioId}/aggiungi-consumabile/`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
    onLogout,
  );

export const staffPersonaggioAggiornaConsumabile = (personaggioId, payload, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${personaggioId}/aggiorna-consumabile/`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
    onLogout,
  );

export const staffPersonaggioRimuoviConsumabile = (personaggioId, consumabileId, motivo, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${personaggioId}/rimuovi-consumabile/`,
    {
      method: 'POST',
      body: JSON.stringify({ consumabile_id: consumabileId, motivo: motivo || '' }),
    },
    onLogout,
  );

export const staffGetOggettoStaff = (id, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/oggetti/${id}/`, { method: 'GET' }, onLogout);

export const staffGetPersonaggioLogs = (id, page = 1, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${id}/logs/?page=${page}`,
    { method: 'GET' },
    onLogout,
  );

export const staffRigeneraLikePersonaggio = (id, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${id}/rigenera-like-influencer/`,
    { method: 'POST' },
    onLogout,
  );

export const staffSendMessageToPersonaggio = (id, titolo, testo, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/personaggi/${id}/send-message/`, {
    method: 'POST',
    body: JSON.stringify({ titolo, testo }),
  }, onLogout);

export const staffGetPersonaggioCreazioneGuidataProposte = (id, onLogout) =>
  fetchAuthenticated(
    `/api/personaggi/api/staff/personaggi/${id}/creazione-guidata/proposte/`,
    { method: 'GET' },
    onLogout,
  );

export const staffGetRegoleTransazioni = (onLogout) =>
  fetchAuthenticated('/api/personaggi/api/staff/regole-transazioni/', { method: 'GET' }, onLogout);

export const staffPatchRegolaTransazione = (id, payload, onLogout) =>
  fetchAuthenticated(`/api/personaggi/api/staff/regole-transazioni/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }, onLogout);

/** Staff: +1 al pool corrente (non oltre il massimo). */
/** delta: positivo aggiunge, negativo toglie (clamp 0…max scheda). Default 1. */
export const staffIncrementaRisorsaPool = (personaggioId, statSigla, motivo, onLogout, delta = 1) =>
    fetchAuthenticated('/api/personaggi/api/staff/risorse-pool/incrementa/', {
        method: 'POST',
        body: JSON.stringify({
            personaggio_id: personaggioId,
            stat_sigla: statSigla,
            motivo: motivo || '',
            delta,
        }),
    }, onLogout);

/**
 * Recupera le proposte tecniche in stato VALUTAZIONE per lo staff.
 */
export const staffGetProposteInValutazione = (onLogout) => {
  return fetchAuthenticated(
    '/api/personaggi/api/staff/proposte/valutazione/', 
    { method: 'GET' }, 
    onLogout
  );
};

/**
 * Rifiuta una proposta tecnica, riportandola in BOZZA e inviando un messaggio.
 */
export const staffRegistraNotaProposta = (propostaId, testo, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/staff/proposta/${propostaId}/nota/`,
    {
      method: 'POST',
      body: JSON.stringify({ testo }),
    },
    onLogout
  );
};

export const staffRifiutaProposta = (propostaId, noteStaff, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/staff/proposta/${propostaId}/rifiuta/`,
    {
      method: 'POST',
      body: JSON.stringify({ note_staff: noteStaff })
    },
    onLogout
  );
};

/**
 * Approva una proposta: crea la tecnica finale, scala i crediti e aggiorna lo stato.
 * 'finalData' contiene i dati definitivi compilati tramite l'editor (Infusione/Tessitura/Cerimoniale).
 */
export const staffApprovaProposta = (propostaId, finalData, onLogout) => {
  return fetchAuthenticated(
    `/api/personaggi/api/staff/proposta/${propostaId}/approva/`,
    {
      method: 'POST',
      body: JSON.stringify(finalData)
    },
    onLogout
  );
};

// --- GESTIONE MOSTRI (TEMPLATE) ---

export const staffGetMostriTemplates = (onLogout) => {
    return fetchAuthenticated('/api/plot/api/staff/mostri-templates/', { method: 'GET' }, onLogout);
};

export const staffCreateMostroTemplate = (data, onLogout) => {
    return fetchAuthenticated('/api/plot/api/staff/mostri-templates/', { 
        method: 'POST', 
        body: JSON.stringify(data) 
    }, onLogout);
};

export const staffUpdateMostroTemplate = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/plot/api/staff/mostri-templates/${id}/`, { 
        method: 'PUT', // Usa PUT per sostituire l'intero oggetto inclusi gli attacchi
        body: JSON.stringify(data) 
    }, onLogout);
};

export const staffDeleteMostroTemplate = (id, onLogout) => {
    return fetchAuthenticated(`/api/plot/api/staff/mostri-templates/${id}/`, { method: 'DELETE' }, onLogout);
};

// --- GESTIONE ABILITÀ ---

export const staffGetAbilitaList = (onLogout, params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('page_size', String(params.pageSize));
    if (params.search) query.set('search', params.search);
    if (params.isTrattoAura !== undefined && params.isTrattoAura !== null) {
        query.set('is_tratto_aura', String(params.isTrattoAura));
    }
    const qs = query.toString();
    const url = `/api/personaggi/api/staff/abilita/${qs ? `?${qs}` : ''}`;
    return fetchAuthenticated(url, { method: 'GET' }, onLogout);
};

/** Elenco completo staff (tutte le pagine) — per combobox che devono includere anche abilità "nascoste in scheda". */
export const staffGetAbilitaListAll = async (onLogout, params = {}) => {
    const pageSize = params.pageSize || 200;
    const all = [];
    let page = 1;
    for (;;) {
        const data = await staffGetAbilitaList(onLogout, { ...params, page, pageSize });
        const rows = Array.isArray(data) ? data : (data?.results || []);
        all.push(...rows);
        if (Array.isArray(data) || !data?.next) break;
        page += 1;
    }
    return all;
};

/** Dettaglio completo (inlines incluse) per l'editor staff — necessario prima di PATCH per non azzerare relazioni. */
export const staffGetAbilita = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/abilita/${id}/`, { method: 'GET' }, onLogout);
};

export const staffCreateAbilita = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/abilita/', {
        method: 'POST',
        body: JSON.stringify(data)
    }, onLogout);
};

export const staffUpdateAbilita = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/abilita/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data)
    }, onLogout);
};

export const staffDeleteAbilita = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/abilita/${id}/`, { method: 'DELETE' }, onLogout);
};

const EDITOR_PREFETCH_TTL_MS = 5 * 60 * 1000;
let abilitaEditorResourcesCache = null;
let abilitaEditorResourcesPromise = null;

const isAbilitaEditorCacheValid = () => {
    if (!abilitaEditorResourcesCache) return false;
    return (Date.now() - abilitaEditorResourcesCache.ts) < EDITOR_PREFETCH_TTL_MS;
};

const fetchAbilitaEditorResources = async (onLogout, { force = false } = {}) => {
    if (!force && isAbilitaEditorCacheValid()) {
        return abilitaEditorResourcesCache.data;
    }
    if (!force && abilitaEditorResourcesPromise) {
        return abilitaEditorResourcesPromise;
    }

    abilitaEditorResourcesPromise = Promise.all([
        fetchAuthenticated('/api/personaggi/api/punteggi/all/', { method: 'GET' }, onLogout),
        fetchAuthenticated('/api/personaggi/api/abilita/', { method: 'GET' }, onLogout),
        fetchAuthenticated('/api/personaggi/api/tier/', { method: 'GET' }, onLogout),
        fetchAuthenticated('/api/personaggi/api/classi_oggetto/', { method: 'GET' }, onLogout),
    ])
        .then(([punteggi, abilita, tiers, classiOggetto]) => {
            const data = {
                punteggi: punteggi || [],
                abilita: abilita || [],
                tiers: tiers || [],
                classiOggetto: Array.isArray(classiOggetto) ? classiOggetto : (classiOggetto?.results || []),
            };
            abilitaEditorResourcesCache = { data, ts: Date.now() };
            return data;
        })
        .finally(() => {
            abilitaEditorResourcesPromise = null;
        });

    return abilitaEditorResourcesPromise;
};

export const prefetchAbilitaEditorResources = (onLogout) => {
    return fetchAbilitaEditorResources(onLogout);
};

export const getAbilitaEditorResources = (onLogout, options = {}) => {
    return fetchAbilitaEditorResources(onLogout, options);
};

// Utile per l'editor: serve la lista di tutte le abilità per i prerequisiti
export const getAbilitaOptions = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/abilita/', { method: 'GET' }, onLogout);
};

export const getTiersList = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/tier/', { method: 'GET' }, onLogout);
};

export const staffCreateOffGame = async (data, onLogout) => {
    return await fetchAuthenticated('/api/plot/api/staff/staff-offgame/', 'POST', data, onLogout);
};

export const staffDeleteOffGame = async (id, onLogout) => {
    return await fetchAuthenticated(`/api/plot/api/staff/staff-offgame/${id}/`, 'DELETE', null, onLogout);
};

// src/api.js - Aggiungi queste esportazioni
export const addFaseToQuest = (payload, onLogout) => fetchAuthenticated('/api/plot/api/fasi/', { method: 'POST', body: JSON.stringify(payload) }, onLogout);
export const removeFaseFromQuest = (id, onLogout) => fetchAuthenticated(`/api/plot/api/fasi/${id}/`, { method: 'DELETE' }, onLogout);
export const addTaskToFase = (payload, onLogout) => fetchAuthenticated('/api/plot/api/tasks/', { method: 'POST', body: JSON.stringify(payload) }, onLogout);
export const updateTask = (id, data, onLogout) =>
    fetchAuthenticated(`/api/plot/api/tasks/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);
export const removeTaskFromFase = (id, onLogout) => fetchAuthenticated(`/api/plot/api/tasks/${id}/`, { method: 'DELETE' }, onLogout);
export const updateFase = (id, data, onLogout) => fetchAuthenticated(`/api/plot/api/fasi/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }, onLogout);



// --- WIKI PAGINE REGOLAMENTO ---

export const getWikiMenu = async () => {
  const token = localStorage.getItem('kor35_token');
  // Puntiamo all'endpoint "smart" definito nelle urlpatterns di gestione_plot/urls.py
  const endpoint = '/api/plot/api/wiki/menu/'; 

  if (token) {
    // Se c'è il token, usiamo fetchAuthenticated: il backend riconoscerà lo Staff 
    // e restituirà anche bozze e pagine riservate.
    return fetchAuthenticated(endpoint, { method: 'GET' });
  } else {
    // Se non c'è token, usiamo fetchPublic: il backend applicherà i filtri di sicurezza.
    return fetchPublic(endpoint);
  }
};

export const getWikiPage = async (slug) => {
  const token = localStorage.getItem('kor35_token');
  const endpoint = `/api/plot/api/wiki/pagina/${slug}/`;

  if (token) {
    return fetchAuthenticated(endpoint, { method: 'GET' });
  } else {
    return fetchPublic(endpoint);
  }
};

const wikiPdfPath = (path) => (API_BASE_URL ? `${API_BASE_URL}${path}` : path);

export const getWikiManualPdfUrl = (manualeSlug = 'completo') => {
  const q = manualeSlug && manualeSlug !== 'completo' ? `?manuale=${encodeURIComponent(manualeSlug)}` : '';
  return wikiPdfPath(`/api/plot/api/wiki/manuale.pdf${q}`);
};

export const getWikiManualLatestPdfUrl = () => getWikiManualeLatestPdfUrl('completo');

export const getWikiManualeLatestPdfUrl = (slug) =>
  wikiPdfPath(`/api/plot/api/wiki/manuali/${encodeURIComponent(slug)}/latest.pdf`);

export const getPublicWikiManualeList = () =>
  fetchPublic('/api/plot/api/wiki/manuali/');

export const getStaffManualePdfList = (onLogout) =>
  fetchAuthenticated('/api/plot/api/staff/manuali-pdf/', { method: 'GET' }, onLogout);

export const createStaffManualePdf = (formData, onLogout) =>
  fetchAuthenticated('/api/plot/api/staff/manuali-pdf/', { method: 'POST', body: formData }, onLogout);

export const updateStaffManualePdf = (slug, formData, onLogout) =>
  fetchAuthenticated(`/api/plot/api/staff/manuali-pdf/${encodeURIComponent(slug)}/`, {
    method: 'PATCH',
    body: formData,
  }, onLogout);

export const generateStaffManualePdf = (slug, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/staff/manuali-pdf/${encodeURIComponent(slug)}/genera/`,
    { method: 'POST', body: '{}' },
    onLogout,
  );

export const getStaffManualePdfAnteprimaUrl = (slug) =>
  wikiPdfPath(`/api/plot/api/staff/manuali-pdf/${encodeURIComponent(slug)}/anteprima/`);

export const getStaffManualePdfExportZipUrl = () =>
  wikiPdfPath('/api/plot/api/staff/manuali-pdf/export-zip/');

export const getWikiPdfDiagnostica = (onLogout) =>
  fetchAuthenticated('/api/plot/api/staff/manuali-pdf/diagnostica/', { method: 'GET' }, onLogout);

/** Metadati sorgenti wiki staff (docs/wiki/staff). */
export const getStaffWikiStaffOpsInfo = (onLogout) =>
  fetchAuthenticated('/api/plot/api/staff/wiki-staff-ops/sync/', { method: 'GET' }, onLogout);

/** Sincronizza pagine Wiki «Operatività tecnica» da repo (force=true sovrascrive). */
export const syncStaffWikiStaffOps = (onLogout, { force = true } = {}) =>
  fetchAuthenticated(
    '/api/plot/api/staff/wiki-staff-ops/sync/',
    { method: 'POST', body: JSON.stringify({ force }) },
    onLogout,
  );

export const getWikiManualeStorico = (slug, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/staff/manuali-pdf/${encodeURIComponent(slug)}/storico/`,
    { method: 'GET' },
    onLogout,
  );

export const startWikiManualeBatchJob = (onLogout) =>
  fetchAuthenticated('/api/plot/api/staff/manuali-pdf/genera-tutti/', {
    method: 'POST',
    body: '{}',
  }, onLogout);

export const getWikiManualeBatchJob = (jobId, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/staff/manuali-pdf/jobs/${jobId}/`,
    { method: 'GET' },
    onLogout,
  );

export const getStaffManualePdfPagine = (slug, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/staff/manuali-pdf/${encodeURIComponent(slug)}/pagine/`,
    { method: 'GET' },
    onLogout,
  );

export const updateStaffManualePdfPagine = (slug, pagine, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/staff/manuali-pdf/${encodeURIComponent(slug)}/pagine/`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pagine }),
    },
    onLogout,
  );

export const getStaffWikiPagesSmall = (onLogout) =>
  fetchAuthenticated('/api/plot/api/staff/pagine-regolamento-small/', { method: 'GET' }, onLogout);

/** @deprecated Preferire generateStaffManualePdf per ogni manuale */
export const generateWikiManualPdfSnapshot = (onLogout) => {
  return fetchAuthenticated('/api/plot/api/wiki/manuale/genera/', { method: 'POST', body: '{}' }, onLogout);
};

/** Lista glossario dichiarazioni per auto-link nelle pagine wiki (cache HTTP lato server). */
export const getWikiGlossario = async () => {
  return fetchPublic('/api/plot/api/public/wiki-glossario/');
};


export const getWikiTable = (id) => {
  return fetchPublic(`/api/plot/api/public/wiki-tabelle/${id}/`);
};

export const getWikiAura = (id) => {
  return fetchPublic(`/api/plot/api/public/wiki-aure/${id}/`);
};

// --- WIDGET TIER: usa tier-display (accetta id Tier oppure id plugin CMS) ---
export const getWikiTier = (id) => {
  return fetchPublic(`/api/plot/api/wiki/tier-display/${encodeURIComponent(String(id))}/`);
};

// --- WIDGET COLLEZIONE TIER ---
export const getWikiTierCollectionDisplay = (id) => {
  const token = localStorage.getItem('kor35_token');
  const endpoint = `/api/plot/api/wiki/tier-collection-display/${encodeURIComponent(String(id))}/`;
  if (token) {
    return fetchAuthenticated(endpoint, { method: 'GET' });
  }
  return fetchPublic(endpoint);
};

export const getWikiTierCollectionWidgetList = () => {
  return fetchPublic('/api/plot/api/public/wiki-tier-collections/');
};

export const getWikiTierCollectionWidget = (id) => {
  return fetchPublic(`/api/plot/api/public/wiki-tier-collections/${id}/`);
};

export const createWikiTierCollectionWidget = (data, onLogout) => {
  return fetchAuthenticated('/api/plot/api/staff/wiki-tier-collections/', {
    method: 'POST',
    body: JSON.stringify(data)
  }, onLogout);
};

export const updateWikiTierCollectionWidget = (id, data, onLogout) => {
  return fetchAuthenticated(`/api/plot/api/staff/wiki-tier-collections/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  }, onLogout);
};

// --- WIDGET MATTONI ---
export const getWikiMattoniWidgetDisplay = (id) => {
  return fetchPublic(`/api/plot/api/wiki/mattoni-display/${encodeURIComponent(String(id))}/`);
};

export const getWikiEraDisplay = (id) => {
  return fetchPublic(`/api/plot/api/wiki/era-display/${encodeURIComponent(String(id))}/`);
};

export const getWikiPunteggi = (tipo) => {
  const t = tipo ? String(tipo).trim() : '';
  return fetchPublic(`/api/plot/api/wiki/punteggi/?tipo=${encodeURIComponent(t)}`);
};

export const getWikiMattoniWidgetList = () => {
  return fetchPublic('/api/plot/api/public/wiki-mattoni-widgets/');
};

export const getWikiMattoniWidget = (id) => {
  return fetchPublic(`/api/plot/api/public/wiki-mattoni-widgets/${id}/`);
};

export const createWikiMattoniWidget = (data, onLogout) => {
  return fetchAuthenticated('/api/plot/api/staff/wiki-mattoni/', {
    method: 'POST',
    body: JSON.stringify(data)
  }, onLogout);
};

export const updateWikiMattoniWidget = (id, data, onLogout) => {
  return fetchAuthenticated(`/api/plot/api/staff/wiki-mattoni/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  }, onLogout);
};

// Helper per avere la lista di tutti i Tier (per l'editor del Master)
export const getWikiTierList = () => {
  return fetchPublic('/api/plot/api/public/wiki-tiers/');
};

// Lista widget Tier configurabili (per inserire / modificare in pagina)
export const getWikiTierWidgetList = () => {
  return fetchPublic('/api/plot/api/public/wiki-tier-widgets/');
};

// Dettaglio singolo widget Tier (per modifica)
export const getWikiTierWidget = (id) => {
  return fetchPublic(`/api/plot/api/public/wiki-tier-widgets/${id}/`);
};

// Crea widget Tier (Staff)
export const createWikiTierWidget = (data, onLogout) => {
  return fetchAuthenticated('/api/plot/api/staff/wiki-tiers/', {
    method: 'POST',
    body: JSON.stringify(data)
  }, onLogout);
};

// Aggiorna widget Tier (Staff)
export const updateWikiTierWidget = (id, data, onLogout) => {
  return fetchAuthenticated(`/api/plot/api/staff/wiki-tiers/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  }, onLogout);
};

// --- WIDGET IMMAGINI WIKI ---
export const getWikiImage = (id) => {
  return fetchPublic(`/api/plot/api/wiki/image-display/${encodeURIComponent(String(id))}/`);
};

export const getWikiImageList = () => {
  return fetchPublic('/api/plot/api/public/wiki-immagini/');
};

// Crea una nuova immagine wiki (Staff) - Supporta FormData per immagini
export const createWikiImage = (formData, onLogout) => {
  return fetchAuthenticated('/api/plot/api/staff/wiki-immagini/', {
    method: 'POST',
    body: formData // fetchAuthenticated gestisce automaticamente il Content-Type per FormData
  }, onLogout);
};

// Aggiorna un'immagine wiki esistente (Staff) - Supporta FormData per immagini
// Usa PATCH per aggiornamenti parziali (non sovrascrive l'immagine se non viene passata)
export const updateWikiImage = (id, formData, onLogout) => {
  return fetchAuthenticated(`/api/plot/api/staff/wiki-immagini/${id}/`, {
    method: 'PATCH',
    body: formData
  }, onLogout);
};

// Elimina un'immagine wiki (Staff)
export const deleteWikiImage = (id, onLogout) => {
  return fetchAuthenticated(`/api/plot/api/staff/wiki-immagini/${id}/`, {
    method: 'DELETE'
  }, onLogout);
};

// --- WIDGET BUTTONS WIKI ---
export const getWidgetButtons = (id) => {
  return fetchPublic(`/api/plot/api/wiki/buttons-display/${encodeURIComponent(String(id))}/`);
};

export const getWidgetButtonsList = () => {
  return fetchPublic('/api/plot/api/public/wiki-buttons/');
};

// Crea un nuovo widget buttons (Staff)
export const createWidgetButtons = (data, onLogout) => {
  return fetchAuthenticated('/api/plot/api/staff/wiki-buttons/', {
    method: 'POST',
    body: JSON.stringify(data)
  }, onLogout);
};

// Aggiorna un widget buttons esistente (Staff)
export const updateWidgetButtons = (id, data, onLogout) => {
  return fetchAuthenticated(`/api/plot/api/staff/wiki-buttons/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  }, onLogout);
};

// Elimina un widget buttons (Staff)
export const deleteWidgetButtons = (id, onLogout) => {
  return fetchAuthenticated(`/api/plot/api/staff/wiki-buttons/${id}/`, {
    method: 'DELETE'
  }, onLogout);
};

// Recupera la lista delle immagini wiki (Staff) - versione autenticata
export const staffGetWikiImages = (onLogout) => {
  return fetchAuthenticated('/api/plot/api/staff/wiki-immagini/', {
    method: 'GET'
  }, onLogout);
};

// --- EVENTI PUBBLICI ---
export const getEventiPubblici = () => {
  return fetchPublic('/api/plot/api/public/eventi/');
};

export const getEventoPubblicoDettaglio = (id) => {
  return fetchPublic(`/api/plot/api/public/eventi/${id}/`);
};

export const geocodeEventoLuogo = (query, onLogout) =>
  fetchAuthenticated(
    `/api/plot/api/eventi/geocode/?q=${encodeURIComponent(query)}`,
    { method: 'GET' },
    onLogout,
  );

// --- CONFIGURAZIONE SITO ---
export const getConfigurazioneSito = () => {
  return fetchPublic('/api/plot/api/public/configurazione-sito/1/');
};

export const getStaffDashboardLayout = (onLogout) =>
  fetchAuthenticated('/api/plot/api/staff/dashboard-layout/', { method: 'GET' }, onLogout);

export const updateStaffDashboardLayout = (layout, onLogout) =>
  fetchAuthenticated('/api/plot/api/staff/dashboard-layout/', {
    method: 'PATCH',
    body: JSON.stringify({ staff_dashboard_layout: layout }),
  }, onLogout);

export const getAdminMaintenanceConfig = (onLogout) => {
  return fetchAuthenticated('/api/plot/api/admin/maintenance-config/', { method: 'GET' }, onLogout);
};

export const updateAdminMaintenanceConfig = (data, onLogout) => {
  return fetchAuthenticated('/api/plot/api/admin/maintenance-config/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  }, onLogout);
};

// --- LINK SOCIAL ---
export const getLinkSocial = () => {
  return fetchPublic('/api/plot/api/public/link-social/');
};

// Crea una nuova pagina (Staff) - Supporta FormData per immagini
export const createWikiPage = (formData, onLogout) => {
  return fetchAuthenticated('/api/plot/api/staff/pagine-regolamento/', {
    method: 'POST',
    body: formData // fetchAuthenticated gestisce automaticamente il Content-Type per FormData
  }, onLogout);
};

// Aggiorna una pagina esistente (Staff)
export const updateWikiPage = (id, formData, onLogout) => {
  return fetchAuthenticated(`/api/plot/api/staff/pagine-regolamento/${id}/`, {
    method: 'PUT',
    body: formData
  }, onLogout);
};

// --- GESTIONE TABELLE (TIER) ---

export const getTiers = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/tiers/', { method: 'GET' }, onLogout);
};

export const createTier = (data, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/tiers/', {
        method: 'POST',
        body: JSON.stringify(data)
    }, onLogout);
};

export const updateTier = (id, data, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/tiers/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data)
    }, onLogout);
};

export const deleteTier = (id, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/tiers/${id}/`, {
        method: 'DELETE'
    }, onLogout);
};

export const getAllAbilitaSimple = (onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/staff/tiers/all_abilita/', { method: 'GET' }, onLogout);
};

export const updateTierAbilita = (tierId, abilitaList, onLogout) => {
    // abilitaList deve essere array di { abilita_id, ordine }
    return fetchAuthenticated(`/api/personaggi/api/staff/tiers/${tierId}/update_abilita_list/`, {
        method: 'POST',
        body: JSON.stringify({ abilita_list: abilitaList })
    }, onLogout);
};

// Helper per ottenere l'URL dell'immagine ottimizzata
// width = 0 ritorna l'originale
export const getWikiImageUrl = (slug, width = 0) => {
    const baseUrl = API_BASE_URL;
    const path = `/api/plot/api/wiki/image/${slug}/?w=${width}`;
    return baseUrl ? `${baseUrl}${path}` : path;
};

/** Origine del backend configurato (per riscrivere URL media assoluti “estranei”). */
function mediaApiOrigin() {
    const raw = (API_BASE_URL || '').trim();
    if (raw) {
        try {
            const href = /^https?:\/\//i.test(raw) ? raw : `http://${raw}`;
            return new URL(href).origin;
        } catch {
            return null;
        }
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }
    return null;
}

/**
 * URL verso il backend per file media (icone, immagini upload, ecc.).
 * - Path relativo → prefisso API_BASE_URL (o origin finestra se base vuota).
 * - URL assoluto con host diverso dal backend configurato → stesso path sull’API corrente
 *   (evita che mirror/dev carichino media da produzione se il JSON contiene ancora URL assoluti).
 */
export const resolveMediaUrl = (path) => {
    if (path == null || path === '') return null;
    const s = String(path).trim();
    if (!s) return null;
    if (s.startsWith('http://') || s.startsWith('https://')) {
        try {
            const mediaUrl = new URL(s);
            const apiOrigin = mediaApiOrigin();
            if (apiOrigin && mediaUrl.origin !== apiOrigin) {
                return `${apiOrigin}${mediaUrl.pathname}${mediaUrl.search}${mediaUrl.hash}`;
            }
            return s;
        } catch {
            return s;
        }
    }
    const cleanBase = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const cleanPath = s.startsWith('/') ? s : `/${s}`;
    return `${cleanBase}${cleanPath}`;
};

// Alias storico (immagini wiki, editor, widget immagine)
export const getMediaUrl = (path) => resolveMediaUrl(path);

/**
 * Registra un nuovo utente.
 */
export const registerUser = async (userData) => {
    // userData deve contenere: username, password, email, first_name, last_name
    return fetchPublic('/api/personaggi/api/user/register/', {
        method: 'POST',
        body: JSON.stringify(userData)
    });
};

/**
 * Cambia la password dell'utente loggato.
 */
export const changePassword = async (oldPassword, newPassword, onLogout) => {
    return fetchAuthenticated('/api/personaggi/api/user/change-password/', {
        method: 'POST',
        body: JSON.stringify({
            old_password: oldPassword,
            new_password: newPassword
        })
    }, onLogout);
};

export const getArcanaPasswordStatus = async (onLogout) => {
    return fetchAuthenticated('/api/auth/arcana/password-status/', { method: 'GET' }, onLogout);
};

export const setArcanaLocalPassword = async (newPassword, confirmPassword, onLogout) => {
    return fetchAuthenticated('/api/auth/arcana/set-local-password/', {
        method: 'POST',
        body: JSON.stringify({
            new_password: newPassword,
            confirm_password: confirmPassword,
        })
    }, onLogout);
};

export const staffGetArcanaProfiles = async (onLogout) => {
    return fetchAuthenticated('/api/auth/arcana/staff/profiles/', { method: 'GET' }, onLogout);
};

/**
 * Attiva un utente (Solo Staff).
 */
export const activateUser = async (userIdToActivate, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/activate-user/${userIdToActivate}/`, {
        method: 'POST'
    }, onLogout);
};

/**
 * Elimina un utente (Solo Staff).
 */
export const deleteUser = async (userIdToDelete, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/delete-user/${userIdToDelete}/`, {
        method: 'DELETE'
    }, onLogout);
};

// --- STAFF: GESTIONE CAMPAGNE ---
export const staffGetCampagne = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/campagne/', { method: 'GET' }, onLogout);

export const staffCreateCampagna = (data, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/campagne/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCampagna = (id, data, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/campagne/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCampagna = (id, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/campagne/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCampagnaUtenti = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/campagne-utenti/', { method: 'GET' }, onLogout);

export const staffCreateCampagnaUtente = (data, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/campagne-utenti/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCampagnaUtente = (id, data, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/campagne-utenti/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCampagnaUtente = (id, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/campagne-utenti/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCampagnaFeaturePolicy = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/campagne-feature-policy/', { method: 'GET' }, onLogout);

export const staffCreateCampagnaFeaturePolicy = (data, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/staff/campagne-feature-policy/', {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCampagnaFeaturePolicy = (id, data, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/campagne-feature-policy/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCampagnaFeaturePolicy = (id, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/staff/campagne-feature-policy/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetUsers = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/users/', { method: 'GET' }, onLogout);

/**
 * Recupera i messaggi indirizzati allo staff (is_staff_message=True)
 */
export const fetchStaffMessages = async (onLogout) => {
    // --- CORREZIONE: Usa l'endpoint specifico per lo staff invece di quello generico ---
    return fetchAuthenticated('/api/personaggi/api/staff/messages/', {
        method: 'GET'
    }, onLogout);
};

/**
 * Marca un messaggio staff come letto.
 */
export const markStaffMessageAsRead = async (messageId, onLogout, letto = true) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/messages/${messageId}/leggi/`, {
        method: 'POST',
        body: JSON.stringify({ letto: Boolean(letto) }),
    }, onLogout);
};

/**
 * Elimina un messaggio staff.
 */
export const deleteStaffMessage = async (messageId, onLogout) => {
    return fetchAuthenticated(`/api/personaggi/api/staff/messages/${messageId}/cancella/`, {
        method: 'POST'
    }, onLogout);
};

/**
 * Recupera conversazioni organizzate per thread.
 */
export const getConversazioni = async (personaggioId, onLogout) => {
    return fetchAuthenticated(
        `/api/personaggi/api/messaggi/conversazioni/?personaggio_id=${personaggioId}`,
        { method: 'GET' },
        onLogout
    );
};

/**
 * Risponde a un messaggio creando un thread.
 */
export const markConversazioneLetta = async (personaggioId, conversazioneId, onLogout) => {
    return fetchAuthenticated(
        '/api/personaggi/api/messaggi/conversazioni/leggi/',
        {
            method: 'POST',
            body: JSON.stringify({
                personaggio_id: personaggioId,
                conversazione_id: conversazioneId,
            }),
        },
        onLogout
    );
};

export const sendGruppoMessage = async (personaggioId, gruppoId, testo, titolo, onLogout) => {
    return fetchAuthenticated(
        '/api/personaggi/api/messaggi/gruppo/send/',
        {
            method: 'POST',
            body: JSON.stringify({
                personaggio_id: personaggioId,
                gruppo_id: gruppoId,
                testo,
                titolo: titolo || '',
            }),
        },
        onLogout
    );
};

export const rispondiMessaggio = async (messaggioId, personaggioId, testo, titolo, onLogout) => {
    return fetchAuthenticated(
        `/api/personaggi/api/messaggi/${messaggioId}/rispondi/`,
        {
            method: 'POST',
            body: JSON.stringify({
                personaggio_id: personaggioId,
                testo,
                titolo
            })
        },
        onLogout
    );
};

// --- Scommesse in-game ---

export const scommesseGetCalendari = (personaggioId, onLogout) => {
    const params = personaggioId ? `?personaggio_id=${encodeURIComponent(personaggioId)}` : '';
    return fetchAuthenticated(`/api/personaggi/api/scommesse/calendari/${params}`, { method: 'GET' }, onLogout);
};

export const scommesseGetConfig = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/scommesse/config/', { method: 'GET' }, onLogout);

export const scommesseGetSquadraStorico = (squadraId, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/scommesse/squadre/${squadraId}/storico/`, { method: 'GET' }, onLogout);

export const scommesseGetClassifiche = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/scommesse/classifiche/', { method: 'GET' }, onLogout);

export const scommesseGetClassificaSport = (sportId, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/scommesse/sport/${sportId}/classifica/`, { method: 'GET' }, onLogout);

export const scommesseGetCalendario = (calendarioId, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/scommesse/calendari/${calendarioId}/`, { method: 'GET' }, onLogout);

export const scommessePiazzaPuntata = (personaggioId, payload, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/scommesse/puntate/', {
        method: 'POST',
        body: JSON.stringify({ ...payload, personaggio_id: personaggioId }),
    }, onLogout);

export const scommesseGetMiePuntate = (personaggioId, onLogout, page = 1, pageSize = 10) => {
    const params = new URLSearchParams();
    params.set('personaggio_id', String(personaggioId));
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
    return fetchAuthenticated(
        `/api/personaggi/api/scommesse/mie-puntate/?${params.toString()}`,
        { method: 'GET' },
        onLogout,
    );
};

export const scommesseRiscuotiVincita = (personaggioId, puntataId, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/scommesse/puntate/${encodeURIComponent(puntataId)}/riscuoti/`,
        {
            method: 'POST',
            body: JSON.stringify({ personaggio_id: personaggioId }),
        },
        onLogout,
    );

export const scommesseRitiraRiserva = (personaggioId, puntataId, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/scommesse/puntate/${encodeURIComponent(puntataId)}/ritira-riserva/`,
        {
            method: 'POST',
            body: JSON.stringify({ personaggio_id: personaggioId }),
        },
        onLogout,
    );

export const scommesseGeneraCodice = (personaggioId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/scommesse/codici/genera/', {
        method: 'POST',
        body: JSON.stringify({ personaggio_id: personaggioId }),
    }, onLogout);

export const scommesseGetMieiCodici = (personaggioId, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/scommesse/miei-codici/?personaggio_id=${encodeURIComponent(personaggioId)}`,
        { method: 'GET' },
        onLogout,
    );

const STAFF_SCOMMESSE = '/api/personaggi/api/staff/scommesse';

export const staffScommesseGetSport = (onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/sport/`, { method: 'GET' }, onLogout);

export const staffScommesseSaveSport = (data, onLogout, id = null) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/sport/${id ? `${id}/` : ''}`, {
        method: id ? 'PATCH' : 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffScommesseDeleteSport = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/sport/${id}/`, { method: 'DELETE' }, onLogout);

export const staffScommesseGetSquadre = (sportId, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/squadre/${sportId ? `?sport=${sportId}` : ''}`, { method: 'GET' }, onLogout);

export const staffScommesseSaveSquadra = (data, onLogout, id = null) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/squadre/${id ? `${id}/` : ''}`, {
        method: id ? 'PATCH' : 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffScommesseDeleteSquadra = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/squadre/${id}/`, { method: 'DELETE' }, onLogout);

export const staffScommesseGetCalendari = (onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/calendari/`, { method: 'GET' }, onLogout);

export const staffScommesseSaveCalendario = (data, onLogout, id = null) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/calendari/${id ? `${id}/` : ''}`, {
        method: id ? 'PATCH' : 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffScommesseDeleteCalendario = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/calendari/${id}/`, { method: 'DELETE' }, onLogout);

export const staffScommesseRigeneraIncontri = (calendarioId, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/calendari/${calendarioId}/rigenera-incontri/`, { method: 'POST' }, onLogout);

export const staffScommesseGetProgrammazioni = (sportId, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/programmazioni/${sportId ? `?sport=${sportId}` : ''}`, { method: 'GET' }, onLogout);

export const staffScommesseSaveProgrammazione = (data, onLogout, id = null) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/programmazioni/${id ? `${id}/` : ''}`, {
        method: id ? 'PATCH' : 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffScommesseDeleteProgrammazione = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/programmazioni/${id}/`, { method: 'DELETE' }, onLogout);

export const staffScommesseSincronizzaProgrammazione = (id, onLogout, maxCrea = 1) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/programmazioni/${id}/sincronizza/`, {
        method: 'POST',
        body: JSON.stringify({ max_crea: maxCrea }),
    }, onLogout);

export const staffScommesseSincronizzaTutteProgrammazioni = (onLogout, maxPerSport = 1) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/programmazioni/sincronizza-tutte/`, {
        method: 'POST',
        body: JSON.stringify({ max_crea_per_sport: maxPerSport }),
    }, onLogout);

export const staffScommesseGeneraCalendarioPerEvento = (programmazioneId, eventoId, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/programmazioni/${programmazioneId}/genera-per-evento/`, {
        method: 'POST',
        body: JSON.stringify({ evento_id: eventoId }),
    }, onLogout);

export const staffScommesseGetConfig = (onLogout, campagnaId = null) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/config/${campagnaId ? `?campagna=${campagnaId}` : ''}`, { method: 'GET' }, onLogout);

export const staffScommesseSaveConfig = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_SCOMMESSE}/config/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

// --- Carte collezionabili ---
const STAFF_CARTE = '/api/personaggi/api/staff/carte';

export const carteGetStato = (charId, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/carte/stato/?char_id=${encodeURIComponent(charId)}`, { method: 'GET' }, onLogout);

export const carteGetCollezione = (charId, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/carte/collezione/?char_id=${encodeURIComponent(charId)}`, { method: 'GET' }, onLogout);

export const carteApriBustina = (charId, bustinaId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/apri-bustina/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, bustina_id: bustinaId }),
    }, onLogout);

export const carteEquipReliquiario = (charId, slotIndex, cartaPossedutaId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/reliquiario/', {
        method: 'POST',
        body: JSON.stringify({
            char_id: charId,
            slot_index: slotIndex,
            carta_posseduta_id: cartaPossedutaId,
        }),
    }, onLogout);

export const carteGetMercato = (charId, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/carte/mercato/?char_id=${encodeURIComponent(charId)}`, { method: 'GET' }, onLogout);

export const carteCreaOffertaMercato = (charId, data, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/mercato/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, ...data }),
    }, onLogout);

export const carteAccettaOffertaMercato = (charId, offertaId, data = {}, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/mercato/accetta/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, offerta_id: offertaId, ...data }),
    }, onLogout);

export const carteAnnullaOffertaMercato = (charId, offertaId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/mercato/annulla/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, offerta_id: offertaId }),
    }, onLogout);

export const MAZZO_DUELLO_SIZE = 15;
export const MAZZI_DUELLO_MAX = 5;

export const carteSaveMazzo = (charId, carteIds, options = {}, onLogout) => {
    const opts = typeof options === 'function' ? {} : (options || {});
    const logout = typeof options === 'function' ? options : onLogout;
    return fetchAuthenticated('/api/personaggi/api/carte/mazzo/', {
        method: 'POST',
        body: JSON.stringify({
            char_id: charId,
            carte_ids: carteIds,
            mazzo_id: opts.mazzoId || null,
            nome: opts.nome,
            is_default: opts.isDefault,
            leader_id: opts.leaderId || null,
        }),
    }, logout);
};

export const carteDeleteMazzo = (charId, mazzoId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/mazzo/', {
        method: 'DELETE',
        body: JSON.stringify({ char_id: charId, mazzo_id: mazzoId }),
    }, onLogout);

export const staffGetCarteCatalogo = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/catalogo/`, { method: 'GET' }, onLogout);

export const staffGetCarteCatalogoByEspansione = (espansioneId, onLogout) =>
    fetchAuthenticated(
        `${STAFF_CARTE}/catalogo/?espansione_id=${encodeURIComponent(espansioneId)}`,
        { method: 'GET' },
        onLogout,
    );

export const staffGetCarteEspansioni = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/espansioni/`, { method: 'GET' }, onLogout);

export const staffCreateCartaEspansione = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/espansioni/`, {
        method: 'POST',
        body: data instanceof FormData ? data : JSON.stringify(data),
    }, onLogout);

export const staffUpdateCartaEspansione = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/espansioni/${id}/`, {
        method: 'PATCH',
        body: data instanceof FormData ? data : JSON.stringify(data),
    }, onLogout);

export const staffDeleteCartaEspansione = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/espansioni/${id}/`, { method: 'DELETE' }, onLogout);

export const staffCreateCartaCatalogo = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/catalogo/`, {
        method: 'POST',
        body: data instanceof FormData ? data : JSON.stringify(data),
    }, onLogout);

export const staffUpdateCartaCatalogo = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/catalogo/${id}/`, {
        method: 'PATCH',
        body: data instanceof FormData ? data : JSON.stringify(data),
    }, onLogout);

export const staffDeleteCartaCatalogo = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/catalogo/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCarteBustine = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/bustine/`, { method: 'GET' }, onLogout);

export const staffGetCarteBustineByEspansione = (espansioneId, onLogout) =>
    fetchAuthenticated(
        `${STAFF_CARTE}/bustine/?espansione_id=${encodeURIComponent(espansioneId)}`,
        { method: 'GET' },
        onLogout,
    );

export const staffCreateCartaBustina = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/bustine/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCartaBustina = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/bustine/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCartaBustina = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/bustine/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCarteConfig = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/config/corrente/`, { method: 'GET' }, onLogout);

export const carteGetDuelli = (charId, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/carte/duello/?char_id=${encodeURIComponent(charId)}`, { method: 'GET' }, onLogout);

export const carteGetAvversariDuello = (charId, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/carte/duello/avversari/?char_id=${encodeURIComponent(charId)}`,
        { method: 'GET' },
        onLogout,
    );

export const carteGetDuello = (charId, duelloId, onLogout) =>
    fetchAuthenticated(
        `/api/personaggi/api/carte/duello/${encodeURIComponent(duelloId)}/?char_id=${encodeURIComponent(charId)}`,
        { method: 'GET' },
        onLogout,
    );

export const carteInvitaDuello = (charId, payload, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/duello/invita/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, ...payload }),
    }, onLogout);

export const carteAccettaDuello = (charId, duelloId, payload, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/carte/duello/${encodeURIComponent(duelloId)}/accetta/`, {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, ...payload }),
    }, onLogout);

export const carteAccettaDuelloCodice = (charId, payload, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/duello/accetta/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, ...payload }),
    }, onLogout);

export const carteAzioneDuello = (charId, duelloId, azione, payload, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/carte/duello/${encodeURIComponent(duelloId)}/azione/`, {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, azione, payload: payload || {} }),
    }, onLogout);

export const carteApriScontro = (charId, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/scontro/apri/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId }),
    }, onLogout);

export const carteUniscitiScontro = (charId, payload, onLogout) =>
    fetchAuthenticated('/api/personaggi/api/carte/scontro/unisciti/', {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, ...payload }),
    }, onLogout);

export const cartePrematchAzione = (charId, duelloId, azione, payload, onLogout) =>
    fetchAuthenticated(`/api/personaggi/api/carte/scontro/${encodeURIComponent(duelloId)}/prematch/`, {
        method: 'POST',
        body: JSON.stringify({ char_id: charId, azione, payload: payload || {} }),
    }, onLogout);

export const staffSaveCarteConfig = (data, onLogout) => {
    if (data?.id) {
        return fetchAuthenticated(`${STAFF_CARTE}/config/${data.id}/`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }, onLogout);
    }
    return fetchAuthenticated(`${STAFF_CARTE}/config/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);
};

export const staffGetCarteEffectSchema = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/effect-schema/`, { method: 'GET' }, onLogout);

export const staffValidateCarteEffectScript = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/effect-schema/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffGetCarteKeywords = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/keywords/`, { method: 'GET' }, onLogout);

export const staffCreateCartaKeyword = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/keywords/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCartaKeyword = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/keywords/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCartaKeyword = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/keywords/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCarteTags = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/tags/`, { method: 'GET' }, onLogout);

export const staffCreateCartaTag = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/tags/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCartaTag = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/tags/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCartaTag = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/tags/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCarteComboReliquiario = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/combo-reliquiario/`, { method: 'GET' }, onLogout);

export const staffCreateCartaComboReliquiario = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/combo-reliquiario/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCartaComboReliquiario = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/combo-reliquiario/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCartaComboReliquiario = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/combo-reliquiario/${id}/`, { method: 'DELETE' }, onLogout);

export const staffGetCarteErrata = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/errata/`, { method: 'GET' }, onLogout);

export const staffCreateCartaErrata = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/errata/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCartaErrata = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/errata/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffDeleteCartaErrata = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/errata/${id}/`, { method: 'DELETE' }, onLogout);

const STAFF_CARTE_PLATFORM = `${STAFF_CARTE}/platform`;

export const staffGetCartePlatformGioco = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE_PLATFORM}/gioco/`, { method: 'GET' }, onLogout);

export const staffCreateCartePlatformGioco = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE_PLATFORM}/gioco/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffUpdateCartePlatformGioco = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE_PLATFORM}/gioco/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffBootstrapCartePlatformGioco = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE_PLATFORM}/gioco/${id}/bootstrap/`, {
        method: 'POST',
        body: JSON.stringify({}),
    }, onLogout);

export const staffGetCartePlatformTemplates = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE_PLATFORM}/templates/`, { method: 'GET' }, onLogout);

export const staffGetCartePlatformRuleset = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE_PLATFORM}/ruleset/`, { method: 'GET' }, onLogout);

export const staffGetCarteScambi = (onLogout, { stato = '', limit = 100 } = {}) => {
    const params = new URLSearchParams();
    if (stato) params.set('stato', stato);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return fetchAuthenticated(`${STAFF_CARTE}/scambi/${qs ? `?${qs}` : ''}`, { method: 'GET' }, onLogout);
};

/** Metadati sorgenti wiki regolamento carte (docs/wiki/carte). */
export const getStaffWikiCarteRegolamentoInfo = (onLogout) =>
    fetchAuthenticated(`${STAFF_CARTE}/wiki-regolamento/sync/`, { method: 'GET' }, onLogout);

/** Sincronizza bozza regolamento carte da repo (force=true sovrascrive). */
export const syncStaffWikiCarteRegolamento = (onLogout, { force = true } = {}) =>
    fetchAuthenticated(
        `${STAFF_CARTE}/wiki-regolamento/sync/`,
        { method: 'POST', body: JSON.stringify({ force }) },
        onLogout,
    );

const CALENDARIO_COMPITI = '/api/plot/api/calendario-compiti';

export const getStaffCompiti = (onLogout, { miei = false } = {}) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/${miei ? '?miei=1' : ''}`, { method: 'GET' }, onLogout);

export const getMieiStaffCompiti = (onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/miei/`, { method: 'GET' }, onLogout);

export const getStaffCompitiCandidati = (onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/candidati/`, { method: 'GET' }, onLogout);

export const createStaffCompito = (data, onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const updateStaffCompito = (id, data, onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const deleteStaffCompito = (id, onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/${id}/`, { method: 'DELETE' }, onLogout);

export const completaStaffCompito = (id, onLogout, { undo = false } = {}) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/${id}/completa/`, {
        method: 'POST',
        body: JSON.stringify({ undo }),
    }, onLogout);

export const getStaffCompitiFeedToken = (onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/feed-token/`, { method: 'GET' }, onLogout);

export const rigeneraStaffCompitiFeedToken = (onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/feed-token/rigenera/`, {
        method: 'POST',
        body: JSON.stringify({}),
    }, onLogout);

export const getStaffCompitiAutomatici = (onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/automatici/`, { method: 'GET' }, onLogout);

export const updateStaffCompitiAutomatici = (items, onLogout) =>
    fetchAuthenticated(`${CALENDARIO_COMPITI}/automatici/`, {
        method: 'PUT',
        body: JSON.stringify({ items }),
    }, onLogout);

const NOTIFICHE_API = '/api/personaggi/api/notifiche';

export const getNotificaPreferenze = (onLogout) =>
    fetchAuthenticated(`${NOTIFICHE_API}/`, { method: 'GET' }, onLogout);

export const patchNotificaPreferenze = (data, onLogout) =>
    fetchAuthenticated(`${NOTIFICHE_API}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const postTelegramLink = (onLogout) =>
    fetchAuthenticated(`${NOTIFICHE_API}/telegram/link/`, {
        method: 'POST',
        body: JSON.stringify({}),
    }, onLogout);

export const postTelegramUnlink = (onLogout) =>
    fetchAuthenticated(`${NOTIFICHE_API}/telegram/unlink/`, {
        method: 'POST',
        body: JSON.stringify({}),
    }, onLogout);

const CONTRATTI_API = '/api/personaggi/api/contratti';
const STAFF_CONTRATTI = '/api/personaggi/api/staff/contratti';

export const contrattiGetAccesso = (charId, onLogout) =>
    fetchAuthenticated(`${CONTRATTI_API}/accesso/?personaggio_id=${encodeURIComponent(charId)}`, { method: 'GET' }, onLogout);

export const contrattiGetScheda = (charId, onLogout) =>
    fetchAuthenticated(`${CONTRATTI_API}/scheda/?personaggio_id=${encodeURIComponent(charId)}`, { method: 'GET' }, onLogout);

export const contrattiCreaProposta = (charId, data, onLogout) =>
    fetchAuthenticated(`${CONTRATTI_API}/proposte/`, {
        method: 'POST',
        body: JSON.stringify({ personaggio_id: charId, ...data }),
    }, onLogout);

export const contrattiAzione = (charId, contrattoId, azione, data, onLogout) =>
    fetchAuthenticated(`${CONTRATTI_API}/${contrattoId}/${azione}/`, {
        method: 'POST',
        body: JSON.stringify({ personaggio_id: charId, ...(data || {}) }),
    }, onLogout);

export const contrattiGetQr = (charId, contrattoId, onLogout) =>
    fetchAuthenticated(`${CONTRATTI_API}/${contrattoId}/qr/?personaggio_id=${encodeURIComponent(charId)}`, { method: 'GET' }, onLogout);

export const staffContrattiGetModelli = (onLogout) =>
    fetchAuthenticated(`${STAFF_CONTRATTI}/modelli/`, { method: 'GET' }, onLogout);

export const staffContrattiCreaModello = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CONTRATTI}/modelli/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffContrattiSalvaModello = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_CONTRATTI}/modelli/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffContrattiEliminaModello = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_CONTRATTI}/modelli/${id}/`, { method: 'DELETE' }, onLogout);

export const staffContrattiGetRuntime = (onLogout) =>
    fetchAuthenticated(`${STAFF_CONTRATTI}/runtime/`, { method: 'GET' }, onLogout);

export const staffContrattiRuntimeAzione = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_CONTRATTI}/runtime/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const getCalendarioFeedToken = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/calendario/feed-token/', { method: 'GET' }, onLogout);

export const postCalendarioFeedTokenRigenera = (onLogout) =>
    fetchAuthenticated('/api/personaggi/api/calendario/feed-token/', {
        method: 'POST',
        body: JSON.stringify({}),
    }, onLogout);

const SONDAGGI_API = '/api/personaggi/api/sondaggi';
const STAFF_SONDAGGI = '/api/personaggi/api/staff/sondaggi';

export const sondaggiGetStato = (charId, onLogout) =>
    fetchAuthenticated(`${SONDAGGI_API}/?personaggio_id=${encodeURIComponent(charId)}&breve=1`, { method: 'GET' }, onLogout);

export const sondaggiGetElenco = (charId, onLogout) =>
    fetchAuthenticated(`${SONDAGGI_API}/?personaggio_id=${encodeURIComponent(charId)}`, { method: 'GET' }, onLogout);

export const sondaggiInviaRisposta = (sondaggioId, charId, risposte, onLogout) =>
    fetchAuthenticated(`${SONDAGGI_API}/${sondaggioId}/risposta/?personaggio_id=${encodeURIComponent(charId)}`, {
        method: 'POST',
        body: JSON.stringify({ risposte }),
    }, onLogout);

export const staffSondaggiGet = (onLogout, id) =>
    fetchAuthenticated(id ? `${STAFF_SONDAGGI}/${id}/` : `${STAFF_SONDAGGI}/`, { method: 'GET' }, onLogout);

export const staffSondaggiImporta = (data, onLogout) =>
    fetchAuthenticated(`${STAFF_SONDAGGI}/`, {
        method: 'POST',
        body: JSON.stringify(data),
    }, onLogout);

export const staffSondaggiPatch = (id, data, onLogout) =>
    fetchAuthenticated(`${STAFF_SONDAGGI}/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }, onLogout);

export const staffSondaggiElimina = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_SONDAGGI}/${id}/`, { method: 'DELETE' }, onLogout);

export const staffSondaggiAggiorna = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_SONDAGGI}/${id}/aggiorna/`, {
        method: 'POST',
        body: JSON.stringify({}),
    }, onLogout);

export const staffSondaggiInoltra = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_SONDAGGI}/${id}/inoltra/`, {
        method: 'POST',
        body: JSON.stringify({}),
    }, onLogout);

export const staffSondaggiPremi = (id, onLogout) =>
    fetchAuthenticated(`${STAFF_SONDAGGI}/${id}/premi/`, {
        method: 'POST',
        body: JSON.stringify({}),
    }, onLogout);